import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUp, ArrowDown, CheckCircle, XCircle, MinusCircle, Loader, Clock, Copy, FileCode, Shield, ShieldAlert, GitBranch, Activity, FileText, Code2, Download, ExternalLink, Eye, RefreshCw, Maximize2, GitFork, Play, Square, Terminal, Github, Upload, Link2, MessageCircle, Paperclip, Send, X, Image, BarChart3, Gauge, Layers, AlertTriangle, Lock, Zap, History, Sparkles, Bot, User, ChevronRight, ChevronDown, Wrench, Palette, Server, BookOpen, GitCommit, Plus, Minus, Database, PenTool, Search, Plug, CreditCard, Share2, Trophy, Gift } from 'lucide-react';
import { useAuth } from '../App';
import { renderEnhancedMarkdown, MessageActions, ExpandableMessage, StopButton, PromptTemplatesPanel, InlineEditForm, saveDraft, loadDraft, clearDraft } from '../components/ChatEnhancements';

// Dynamic confetti import — won't break build if package missing
let confettiLib = null;
try { confettiLib = require('canvas-confetti'); } catch (e) { /* optional */ }
const fireConfetti = confettiLib ? (confettiLib.default || confettiLib) : null;

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Safety declarations for variables that may be referenced by cached/older deployed code
// These prevent ReferenceError crashes in the ErrorBoundary during cache transitions
const NotificationCenter = () => null;
const NotificationProvider = ({ children }) => children;
const useNotifications = () => ({ notifications: [], addNotification: () => {}, clearNotifications: () => {} });
// Components that may be referenced by older cached versions
const BranchSelector = () => null;
const SafeMarkdown = ({ children }) => children || null;
const AgentPipeline = () => null;
const AgentIcon = () => null;
const StageIcon = () => null;
const StepIcon = () => null;
const Tag = ({ children }) => children || null;
const ReactMarkdown = ({ children }) => children || null;
const TestRunner = () => null;
const DeployPanel = () => null;
const CodeEditor = () => null;
const FileTree = () => null;
const LivePreview = () => null;
const ChatPanel = () => null;
const BuildProgress = () => null;
const VersionHistory = () => null;
const SecurityScan = () => null;
const CommitGraph = () => null;
const PipelineView = () => null;
const SettingsPanel = () => null;
const MergeConflictResolver = () => null;
const PRReview = () => null;
const EnvironmentSelector = () => null;
// State-like variables that older cached renders may reference
let codesPanelExpanded = false;
let setCodesPanelExpanded = () => {};
let artifactsPanelOpen = false;
let setArtifactsPanelOpen = () => {};
let previewUrl = '';
let setPreviewUrl = () => {};
let sandboxReady = false;
let setSandboxReady = () => {};
let chatSearchOpen = false;
let setChatSearchOpen = () => {};
let chatSearchQuery = '';
let setChatSearchQuery = () => {};
let filteredMessages = [];
let showCodePanel = false;
let setShowCodePanel = () => {};
let selectedFile = null;
let setSelectedFile = () => {};
let codeViewerOpen = false;
let setCodeViewerOpen = () => {};
let splitView = false;
let setSplitView = () => {};
let terminalOpen = false;
let setTerminalOpen = () => {};
let terminalOutput = '';
let setTerminalOutput = () => {};
let bookmarkedMessages = [];
let setBookmarkedMessages = () => {};
let messageReactions = {};
let setMessageReactions = () => {};
let threadView = false;
let setThreadView = () => {};
let pinnedMessages = [];
let setPinnedMessages = () => {};
let chatFontSize = 14;
let setChatFontSize = () => {};
let chatTheme = 'dark';
let setChatTheme = () => {};
let autoScroll = true;
let setAutoScroll = () => {};
let compactMode = false;
let setCompactMode = () => {};
let showTimestamps = true;
let setShowTimestamps = () => {};
let expandedSections = {};
let setExpandedSections = () => {};
let sidebarCollapsed = false;
let setSidebarCollapsed = () => {};
let activePanel = 'chat';
let setActivePanel = () => {};
let editorTheme = 'dark';
let setEditorTheme = () => {};
let fontSize = 14;
let setFontSize = () => {};
let wordWrap = true;
let setWordWrap = () => {};
let minimap = false;
let setMinimap = () => {};
let lineNumbers = true;
let setLineNumbers = () => {};
let diff = null;
let setDiff = () => {};
let currentFileIndex = 0;
let setCurrentFileIndex = () => {};
let sseRef = { current: null };
let branchName = 'main';
let setBranchName = () => {};
let branches = [];
let setBranches = () => {};
let selectedBranch = 'main';
let setSelectedBranch = () => {};
let commitHistory = [];
let setCommitHistory = () => {};
let isDeploying = false;
let setIsDeploying = () => {};
let deployStatus = null;
let setDeployStatus = () => {};
let testResults = null;
let setTestResults = () => {};
let securityResults = null;
let setSecurityResults = () => {};
let livePreviewUrl = '';
let setLivePreviewUrl = () => {};
let activeTab = 'chat';
let setActiveTab = () => {};

// Global safety net: intercept ALL ReferenceError "X is not defined" and redirect to /history
if (typeof window !== 'undefined' && !window.__cfSafetyInstalled) {
  window.__cfSafetyInstalled = true;
  window.addEventListener('error', (event) => {
    if (event.error instanceof ReferenceError && event.error.message && event.error.message.includes('is not defined')) {
      event.preventDefault();
      if (window.location.pathname !== '/history') {
        window.location.replace('/history');
      }
      return false;
    }
  }, true);
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason instanceof ReferenceError && event.reason.message && event.reason.message.includes('is not defined')) {
      event.preventDefault();
    }
  });
}

const PIPELINE_STEPS = [
  { key: 'IDEA', label: 'Idea' },
  { key: 'INTAKE', label: 'Intake' },
  { key: 'PLANNING', label: 'Planning' },
  { key: 'PRD', label: 'PRD' },
  { key: 'ARCH', label: 'Architecture' },
  { key: 'DATABASE', label: 'Database' },
  { key: 'API_DESIGN', label: 'API Design' },
  { key: 'UIUX_DESIGN', label: 'UI/UX' },
  { key: 'CODE', label: 'Code' },
  { key: 'CODE_REVIEW', label: 'Review' },
  { key: 'INTEGRATION', label: 'Integrate' },
  { key: 'TEST', label: 'Testing' },
  { key: 'SECURITY', label: 'Security' },
  { key: 'DOCS', label: 'Docs' },
  { key: 'DEVOPS', label: 'Deploy' },
  { key: 'MONITOR', label: 'Monitor' },
  { key: 'SELF_FIX', label: 'Self-Fix' },
  { key: 'DONE', label: 'Done' },
];

// Map pipeline step keys to their artifact keys in the run data
const STEP_ARTIFACT_MAP = {
  'PLANNING': 'plan',
  'PRD': 'prd',
  'ARCH': 'architecture',
  'DATABASE': 'database',
  'API_DESIGN': 'api_design',
  'UIUX_DESIGN': 'uiux_design',
  'CODE': 'code',
  'CODE_REVIEW': 'code_review',
  'INTEGRATION': 'integration',
  'TEST': 'tests',
  'SECURITY': 'security',
  'DOCS': 'documentation',
  'DEVOPS': 'deployment',
  'MONITOR': 'monitoring',
};

// Step icons mapping
const STEP_ICONS = {
  'IDEA': Sparkles,
  'INTAKE': MessageCircle,
  'PLANNING': Layers,
  'PRD': FileText,
  'ARCH': Server,
  'DATABASE': Database,
  'API_DESIGN': Code2,
  'UIUX_DESIGN': Palette,
  'CODE': FileCode,
  'CODE_REVIEW': Search,
  'INTEGRATION': Plug,
  'TEST': Activity,
  'SECURITY': Shield,
  'DOCS': BookOpen,
  'DEVOPS': GitBranch,
  'MONITOR': Gauge,
  'SELF_FIX': Zap,
  'DONE': CheckCircle,
};

// Agent color themes — each agent gets a distinct accent
const AGENT_COLORS = {
  'Planning Agent': { bg: '#1e1b4b', accent: '#818cf8', ring: 'rgba(129,140,248,0.3)' },
  'Product Agent': { bg: '#1c1917', accent: '#f59e0b', ring: 'rgba(245,158,11,0.3)' },
  'Architecture Agent': { bg: '#0c4a6e', accent: '#38bdf8', ring: 'rgba(56,189,248,0.3)' },
  'Database Agent': { bg: '#14532d', accent: '#4ade80', ring: 'rgba(74,222,128,0.3)' },
  'API Design Agent': { bg: '#312e81', accent: '#a78bfa', ring: 'rgba(167,139,250,0.3)' },
  'UI/UX Design Agent': { bg: '#831843', accent: '#f472b6', ring: 'rgba(244,114,182,0.3)' },
  'Coding Agent': { bg: '#0f172a', accent: '#6366f1', ring: 'rgba(99,102,241,0.3)' },
  'Code Review Agent': { bg: '#422006', accent: '#fb923c', ring: 'rgba(251,146,60,0.3)' },
  'Integration Agent': { bg: '#1e3a5f', accent: '#67e8f9', ring: 'rgba(103,232,249,0.3)' },
  'Testing Agent': { bg: '#052e16', accent: '#34d399', ring: 'rgba(52,211,153,0.3)' },
  'Security Agent': { bg: '#450a0a', accent: '#f87171', ring: 'rgba(248,113,113,0.3)' },
  'Documentation Agent': { bg: '#1a1a2e', accent: '#94a3b8', ring: 'rgba(148,163,184,0.3)' },
  'DevOps Agent': { bg: '#1c1917', accent: '#a3e635', ring: 'rgba(163,230,53,0.3)' },
  'Monitoring Agent': { bg: '#1e1b4b', accent: '#c084fc', ring: 'rgba(192,132,252,0.3)' },
  // Legacy agent names
  'CodeFoundry': { bg: '#0f172a', accent: '#818cf8', ring: 'rgba(129,140,248,0.2)' },
  'CodeFoundry Planner': { bg: '#1e1b4b', accent: '#818cf8', ring: 'rgba(129,140,248,0.3)' },
  'CodeFoundry Healer': { bg: '#1e1b4b', accent: '#a78bfa', ring: 'rgba(167,139,250,0.3)' },
  'default': { bg: '#0f172a', accent: '#818cf8', ring: 'rgba(129,140,248,0.2)' },
};

// Map agent names to their step icons
const AGENT_ICON_MAP = {
  'Planning Agent': Layers,
  'Product Agent': FileText,
  'Architecture Agent': Server,
  'Database Agent': Database,
  'API Design Agent': Code2,
  'UI/UX Design Agent': Palette,
  'Coding Agent': FileCode,
  'Code Review Agent': Search,
  'Integration Agent': Plug,
  'Testing Agent': Activity,
  'Security Agent': Shield,
  'Documentation Agent': BookOpen,
  'DevOps Agent': GitBranch,
  'Monitoring Agent': Gauge,
  // Legacy
  'CodeFoundry': Bot,
  'CodeFoundry Planner': Layers,
  'CodeFoundry Healer': Zap,
};

// ─── Diff Viewer Component ───
function DiffViewer({ oldCode, newCode, fileName }) {
  if (!oldCode && !newCode) return null;
  const oldLines = (oldCode || '').split('\n');
  const newLines = (newCode || '').split('\n');

  // Simple line-by-line diff
  const maxLen = Math.max(oldLines.length, newLines.length);
  const diffLines = [];
  for (let i = 0; i < maxLen; i++) {
    const oldLine = oldLines[i];
    const newLine = newLines[i];
    if (oldLine === undefined) {
      diffLines.push({ type: 'add', line: newLine, num: i + 1 });
    } else if (newLine === undefined) {
      diffLines.push({ type: 'remove', line: oldLine, num: i + 1 });
    } else if (oldLine !== newLine) {
      diffLines.push({ type: 'remove', line: oldLine, num: i + 1 });
      diffLines.push({ type: 'add', line: newLine, num: i + 1 });
    } else {
      diffLines.push({ type: 'same', line: oldLine, num: i + 1 });
    }
  }

  const added = diffLines.filter(d => d.type === 'add').length;
  const removed = diffLines.filter(d => d.type === 'remove').length;

  return (
    <div data-testid="diff-viewer" style={{
      background: '#0d1117', borderRadius: '0.5rem', overflow: 'hidden',
      border: '1px solid rgba(255,255,255,0.06)', fontSize: '0.75rem',
    }}>
      <div style={{
        padding: '0.5rem 0.75rem', background: 'rgba(255,255,255,0.03)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ color: '#8b949e', fontFamily: 'monospace' }}>{fileName || 'diff'}</span>
        <span style={{ display: 'flex', gap: '0.5rem', fontSize: '0.6875rem' }}>
          <span style={{ color: '#3fb950' }}>+{added}</span>
          <span style={{ color: '#f85149' }}>-{removed}</span>
        </span>
      </div>
      <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '0.25rem 0' }}>
        {diffLines.slice(0, 100).map((d, i) => (
          <div key={i} style={{
            display: 'flex', fontFamily: 'monospace', lineHeight: '1.4',
            background: d.type === 'add' ? 'rgba(63,185,80,0.1)' :
                         d.type === 'remove' ? 'rgba(248,81,73,0.1)' : 'transparent',
            borderLeft: d.type === 'add' ? '3px solid #3fb950' :
                         d.type === 'remove' ? '3px solid #f85149' : '3px solid transparent',
          }}>
            <span style={{ width: '32px', textAlign: 'right', padding: '0 6px', color: '#484f58', flexShrink: 0, userSelect: 'none' }}>{d.num}</span>
            <span style={{ padding: '0 4px', color: d.type === 'add' ? '#3fb950' : d.type === 'remove' ? '#f85149' : '#c9d1d9', flexShrink: 0, width: '14px' }}>
              {d.type === 'add' ? '+' : d.type === 'remove' ? '-' : ' '}
            </span>
            <span style={{ padding: '0 4px', color: '#c9d1d9', whiteSpace: 'pre', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.line}</span>
          </div>
        ))}
        {diffLines.length > 100 && (
          <div style={{ padding: '0.5rem', textAlign: 'center', color: '#484f58', fontSize: '0.6875rem' }}>
            ... {diffLines.length - 100} more lines
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Artifact Preview Component ───
function ArtifactPreview({ stepKey, artifact }) {
  if (!artifact || (typeof artifact === 'object' && Object.keys(artifact).length === 0)) {
    return <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', padding: '0.5rem', fontStyle: 'italic' }}>No data generated for this step.</div>;
  }

  const previewStyle = {
    fontSize: '0.6875rem', lineHeight: '1.5', color: 'hsl(var(--foreground))',
    maxHeight: '250px', overflowY: 'auto', padding: '0.5rem',
    background: 'hsl(var(--secondary))', borderRadius: '0.375rem',
  };

  if (stepKey === 'API_DESIGN') {
    const endpoints = artifact.endpoints || [];
    return (
      <div style={previewStyle} data-testid="artifact-api-design">
        <div style={{ fontWeight: 600, marginBottom: '0.375rem' }}>Auth: {artifact.auth_strategy || 'JWT'}</div>
        {endpoints.map((group, i) => (
          <div key={i} style={{ marginBottom: '0.375rem' }}>
            <div style={{ fontWeight: 600, color: '#6366f1', fontSize: '0.625rem', textTransform: 'uppercase' }}>{group.group}</div>
            {(group.endpoints || []).slice(0, 4).map((ep, j) => (
              <div key={j} style={{ display: 'flex', gap: '0.375rem', padding: '0.125rem 0' }}>
                <span style={{
                  fontFamily: 'monospace', fontSize: '0.625rem', fontWeight: 600, padding: '0 4px', borderRadius: '3px',
                  background: ep.method === 'GET' ? 'rgba(16,185,129,0.15)' : ep.method === 'POST' ? 'rgba(59,130,246,0.15)' : ep.method === 'PUT' ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                  color: ep.method === 'GET' ? '#10b981' : ep.method === 'POST' ? '#3b82f6' : ep.method === 'PUT' ? '#f59e0b' : '#ef4444',
                }}>{ep.method}</span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.625rem' }}>{ep.path}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (stepKey === 'UIUX_DESIGN') {
    const pages = artifact.pages || [];
    const colors = artifact.design_system?.color_palette || {};
    return (
      <div style={previewStyle} data-testid="artifact-uiux-design">
        <div style={{ display: 'flex', gap: '4px', marginBottom: '0.375rem', flexWrap: 'wrap' }}>
          {Object.entries(colors).slice(0, 6).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.5625rem' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: v, border: '1px solid rgba(255,255,255,0.1)' }} />
              <span style={{ color: 'hsl(var(--muted-foreground))' }}>{k}</span>
            </div>
          ))}
        </div>
        {pages.slice(0, 5).map((p, i) => (
          <div key={i} style={{ padding: '0.125rem 0' }}>
            <span style={{ fontWeight: 600 }}>{p.name}</span>
            <span style={{ color: 'hsl(var(--muted-foreground))', marginLeft: '0.25rem', fontSize: '0.625rem' }}>{p.route}</span>
          </div>
        ))}
      </div>
    );
  }

  if (stepKey === 'DATABASE') {
    const collections = artifact.collections || [];
    return (
      <div style={previewStyle} data-testid="artifact-database">
        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Engine: {artifact.engine || 'MongoDB'}</div>
        {collections.slice(0, 6).map((col, i) => (
          <div key={i} style={{ padding: '0.125rem 0', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Database size={10} style={{ color: '#6366f1', flexShrink: 0 }} />
            <span style={{ fontWeight: 600 }}>{col.name}</span>
            <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.5625rem' }}>({(col.fields || []).length} fields)</span>
          </div>
        ))}
      </div>
    );
  }

  if (stepKey === 'PLANNING') {
    const features = artifact.features || [];
    return (
      <div style={previewStyle} data-testid="artifact-planning">
        {features.slice(0, 6).map((f, i) => (
          <div key={i} style={{ padding: '0.125rem 0', display: 'flex', gap: '0.25rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '0.5625rem', fontWeight: 600, padding: '1px 4px', borderRadius: '3px',
              background: f.priority === 'P0' ? 'rgba(239,68,68,0.15)' : f.priority === 'P1' ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.15)',
              color: f.priority === 'P0' ? '#ef4444' : f.priority === 'P1' ? '#f59e0b' : '#6366f1',
            }}>{f.priority || 'P2'}</span>
            <span style={{ fontWeight: 500 }}>{f.name}</span>
          </div>
        ))}
      </div>
    );
  }

  if (stepKey === 'SECURITY') {
    return (
      <div style={previewStyle} data-testid="artifact-security">
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.25rem' }}>
          <span>Risk: <strong style={{ color: artifact.risk_level === 'Low' ? '#10b981' : '#f59e0b' }}>{artifact.risk_level}</strong></span>
          <span>Score: <strong>{artifact.risk_score}/100</strong></span>
        </div>
        {artifact.findings && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {Object.entries(artifact.findings).map(([k, v]) => (
              <span key={k} style={{ fontSize: '0.625rem', padding: '1px 6px', borderRadius: '3px',
                background: k === 'critical' ? 'rgba(239,68,68,0.15)' : k === 'high' ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.08)',
                color: k === 'critical' ? '#ef4444' : k === 'high' ? '#f59e0b' : 'hsl(var(--muted-foreground))',
              }}>{k}: {v}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (stepKey === 'CODE') {
    const files = artifact.files || [];
    return (
      <div style={previewStyle} data-testid="artifact-code">
        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{files.length} files generated</div>
        {files.slice(0, 8).map((f, i) => (
          <div key={i} style={{ padding: '1px 0', fontFamily: 'monospace', fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>
            {f.path}
          </div>
        ))}
        {files.length > 8 && <div style={{ fontSize: '0.5625rem', color: 'hsl(var(--muted-foreground))' }}>+{files.length - 8} more</div>}
      </div>
    );
  }

  if (stepKey === 'CODE_REVIEW') {
    const grade = artifact.overall_grade || 'N/A';
    const score = artifact.overall_score || 0;
    const findings = artifact.findings || [];
    const catScores = artifact.category_scores || {};
    const blocking = artifact.blocking_issues || [];
    return (
      <div style={previewStyle} data-testid="artifact-code-review">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
          <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>Grade: <span style={{ color: grade.startsWith('A') ? '#10b981' : grade === 'B' ? '#3b82f6' : grade === 'C' ? '#f59e0b' : '#ef4444' }}>{grade}</span></span>
          <span style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>{score}%</span>
        </div>
        {Object.entries(catScores).slice(0, 5).map(([cat, s]) => (
          <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0', fontSize: '0.625rem' }}>
            <span style={{ textTransform: 'capitalize' }}>{cat.replace('_', ' ')}</span>
            <span style={{ fontWeight: 600, color: s >= 80 ? '#10b981' : s >= 60 ? '#f59e0b' : '#ef4444' }}>{s}%</span>
          </div>
        ))}
        {findings.length > 0 && (
          <div style={{ marginTop: '0.25rem', fontSize: '0.5625rem', color: 'hsl(var(--muted-foreground))' }}>
            {findings.length} finding(s) | {blocking.length} blocking
          </div>
        )}
      </div>
    );
  }

  if (stepKey === 'INTEGRATION') {
    const detected = artifact.detected_integrations || [];
    const intFiles = artifact.integration_files || [];
    const envVars = artifact.env_vars || [];
    return (
      <div style={previewStyle} data-testid="artifact-integration">
        {detected.length > 0 ? (
          <>
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{detected.length} Integration(s) Detected</div>
            {detected.map((d, i) => (
              <div key={i} style={{ padding: '2px 0', display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                <Plug size={10} style={{ color: '#6366f1', flexShrink: 0 }} />
                <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{d.category}</span>
                <span style={{ fontSize: '0.5625rem', color: 'hsl(var(--muted-foreground))' }}>({d.suggested})</span>
              </div>
            ))}
            {intFiles.length > 0 && (
              <div style={{ marginTop: '0.25rem', fontSize: '0.5625rem', color: '#10b981' }}>
                {intFiles.length} file(s) generated
              </div>
            )}
            {envVars.length > 0 && (
              <div style={{ fontSize: '0.5625rem', color: 'hsl(var(--muted-foreground))' }}>
                {envVars.length} env var(s) needed
              </div>
            )}
          </>
        ) : (
          <div style={{ fontStyle: 'italic', color: 'hsl(var(--muted-foreground))' }}>No integrations detected</div>
        )}
      </div>
    );
  }

  // Default: JSON preview
  const jsonStr = JSON.stringify(artifact, null, 2);
  return (
    <div style={previewStyle} data-testid="artifact-default">
      <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.625rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {jsonStr.slice(0, 1000)}{jsonStr.length > 1000 ? '\n...' : ''}
      </pre>
    </div>
  );
}

// ─── GitHub Commit Timeline Component ───
function GitHubTimeline({ pushResult, pushLoading }) {
  if (!pushResult && !pushLoading) return null;

  const steps = [];
  if (pushLoading) {
    steps.push({ label: 'Connecting to repository...', status: 'active', icon: Github });
    steps.push({ label: 'Preparing files', status: 'pending', icon: FileCode });
    steps.push({ label: 'Committing changes', status: 'pending', icon: GitCommit });
    steps.push({ label: 'Pushing to remote', status: 'pending', icon: Upload });
  } else if (pushResult) {
    steps.push({ label: `Connected to ${pushResult.repo || 'repo'}`, status: 'done', icon: Github });
    steps.push({ label: `${pushResult.files_pushed || pushResult.files_count || 0} files prepared`, status: 'done', icon: FileCode });
    steps.push({ label: pushResult.commit_sha ? `Committed ${pushResult.commit_sha.slice(0, 7)}` : 'Committed', status: 'done', icon: GitCommit });
    steps.push({ label: pushResult.branch ? `Pushed to ${pushResult.branch}` : 'Pushed', status: 'done', icon: Upload });
    if (pushResult.deployed_url) {
      steps.push({ label: 'Deployed', status: 'done', icon: ExternalLink, url: pushResult.deployed_url });
    }
  }

  return (
    <div data-testid="github-timeline" style={{
      padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem',
    }}>
      <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.625rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <GitBranch size={12} /> Deployment Timeline
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{
                  width: '22px', height: '22px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: step.status === 'done' ? '#10b981' : step.status === 'active' ? '#6366f1' : 'hsl(var(--border))',
                  transition: 'all 0.3s',
                }}>
                  {step.status === 'active'
                    ? <Loader size={11} style={{ color: '#fff', animation: 'spin 1s linear infinite' }} />
                    : <Icon size={11} style={{ color: step.status === 'done' ? '#fff' : 'hsl(var(--muted-foreground))' }} />}
                </div>
                {i < steps.length - 1 && (
                  <div style={{
                    width: '2px', height: '18px', flexShrink: 0,
                    background: step.status === 'done' ? '#10b981' : 'hsl(var(--border))',
                    transition: 'background 0.3s',
                  }} />
                )}
              </div>
              <div style={{ paddingTop: '2px', paddingBottom: '0.375rem', flex: 1 }}>
                {step.url ? (
                  <a href={step.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.6875rem', fontWeight: 500, color: '#6366f1', textDecoration: 'none' }}>
                    {step.label} <ExternalLink size={9} style={{ display: 'inline', verticalAlign: 'middle' }} />
                  </a>
                ) : (
                  <span style={{
                    fontSize: '0.6875rem', fontWeight: step.status === 'active' ? 600 : 400,
                    color: step.status === 'done' ? 'hsl(var(--foreground))' : step.status === 'active' ? '#6366f1' : 'hsl(var(--muted-foreground))',
                  }}>{step.label}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshCredits, user } = useAuth();
  const [project, setProject] = useState(null);
  const [logs, setLogs] = useState([]);
  const [artifacts, setArtifacts] = useState({});
  const [activeTab, setActiveTab] = useState('chat');
  const [selectedFile, setSelectedFile] = useState(null);
  const [copied, setCopied] = useState(false);
  const logRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [previewKey, setPreviewKey] = useState(0);
  const [forkLoading, setForkLoading] = useState(false);
  const [forkIdea, setForkIdea] = useState('');
  const [showForkForm, setShowForkForm] = useState(false);
  const [sandboxStatus, setSandboxStatus] = useState('not_running');
  const [sandboxInfo, setSandboxInfo] = useState(null);
  const [sandboxLoading, setSandboxLoading] = useState(false);
  const [sandboxLogs, setSandboxLogs] = useState('');
  const [githubStatus, setGithubStatus] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState('');
  const [showGitPanel, setShowGitPanel] = useState(false);
  const [error, setError] = useState(null);
  const [repoName, setRepoName] = useState('');
  const [isPrivateRepo, setIsPrivateRepo] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [repos, setRepos] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('main');
  const [repoSearch, setRepoSearch] = useState('');
  const [showNewRepo, setShowNewRepo] = useState(false);
  const [newRepoName, setNewRepoName] = useState('');
  const [creatingRepo, setCreatingRepo] = useState(false);
  const [buildIntel, setBuildIntel] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionDiff, setVersionDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [versionFiles, setVersionFiles] = useState(null);
  const [versionView, setVersionView] = useState('timeline'); // 'timeline', 'files', 'diff'
  const [deployStatus, setDeployStatus] = useState(null);
  const [deployLoading, setDeployLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatImages, setChatImages] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatMode, setChatMode] = useState('chat'); // 'chat' or 'feature'
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [failedSince, setFailedSince] = useState(null); // Track when status first became 'failed'
  const [vibeTick, setVibeTick] = useState(0); // Triggers re-render to cycle vibe messages
  const [checkpointResponding, setCheckpointResponding] = useState(false);
  const [respondedCheckpoints, setRespondedCheckpoints] = useState(new Set());
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const sseRef = useRef(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [verification, setVerification] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState(new Set());
  const [toolLogs, setToolLogs] = useState([]);
  const [buildAnalytics, setBuildAnalytics] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [celebrationDone, setCelebrationDone] = useState(false);
  const [editingMsgIndex, setEditingMsgIndex] = useState(null);
  const [showPromptTemplates, setShowPromptTemplates] = useState(false);

  // Load draft from localStorage on mount
  useEffect(() => {
    const draft = loadDraft(id);
    if (draft) setChatInput(draft);
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projRes = await fetch(`${API_URL}/api/projects/${id}`, { credentials: 'include' }).then(r => r.json());
        if (projRes.detail) {
          // Don't show error on first load — backend may be warming up, retry silently
          if (!project) {
            console.warn('Project load failed, retrying...', projRes.detail);
            return;
          }
          setError(projRes.detail);
          return;
        }
        setProject(projRes);

        // Fetch logs and artifacts independently (don't block on failure)
        try {
          const logRes = await fetch(`${API_URL}/api/projects/${id}/logs`, { credentials: 'include' }).then(r => r.json());
          setLogs(logRes.logs || []);
        } catch {}
        try {
          const artRes = await fetch(`${API_URL}/api/projects/${id}/artifacts`, { credentials: 'include' }).then(r => r.json());
          setArtifacts(artRes.artifacts || {});
        } catch {}
      } catch (err) {
        console.error(err);
        // Don't show error on first load — will retry via interval
        if (project) {
          setError('Failed to load project');
        }
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => {
      clearInterval(interval);
      // Cleanup SSE connection if any
      if (sseRef.current) { try { sseRef.current.close(); } catch {} sseRef.current = null; }
    };
  }, [id]);

  // Fetch build intelligence when project exists
  useEffect(() => {
    if (project) {
      fetch(`${API_URL}/api/projects/${id}/build-intelligence`, { credentials: 'include' }).then(r => r.json()).then(d => setBuildIntel(d)).catch(() => {});
      // Also fetch deploy status
      fetch(`${API_URL}/api/projects/${id}/deploy/status`, { credentials: 'include' }).then(r => r.json()).then(d => {
        if (d.status && d.status !== 'none') setDeployStatus(d);
      }).catch(() => {});
    }
  }, [id, project?.status]);

  // Celebration confetti when build completes
  useEffect(() => {
    if (project?.status === 'completed' && !celebrationDone) {
      setCelebrationDone(true);
      // Staggered confetti bursts (safe — works even if confetti not loaded)
      if (fireConfetti) {
        const fire = (opts) => fireConfetti({ particleCount: 80, spread: 100, origin: { y: 0.6 }, ...opts });
        fire({ angle: 60, origin: { x: 0 } });
        setTimeout(() => fire({ angle: 120, origin: { x: 1 } }), 300);
        setTimeout(() => fire({ angle: 90, origin: { x: 0.5 } }), 600);
        setTimeout(() => fireConfetti({ particleCount: 150, spread: 160, origin: { y: 0.35 }, colors: ['#6366f1', '#818cf8', '#c7d2fe', '#10b981', '#fbbf24'] }), 1000);
      }
      // Fetch analytics and referral data
      fetch(`${API_URL}/api/projects/${id}/analytics`, { credentials: 'include' }).then(r => r.json()).then(setBuildAnalytics).catch(() => {});
      fetch(`${API_URL}/api/referral`, { credentials: 'include' }).then(r => r.json()).then(setReferralData).catch(() => {});
    }
  }, [project?.status, id, celebrationDone]);
  useEffect(() => {
    if (activeTab === 'versions' || project?.status === 'completed') {
      fetch(`${API_URL}/api/projects/${id}/versions`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => setVersions(d.versions || []))
        .catch(() => {});
    }
  }, [id, activeTab, project?.status]);

  useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Check sandbox status when Preview tab is selected
  useEffect(() => {
    if (activeTab === 'preview' && project) {
      checkSandboxStatus();
    }
  }, [activeTab, project]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check GitHub status when Git tab is selected
  useEffect(() => {
    if (activeTab === 'git') {
      fetchGitHubStatus();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load chat messages when Chat tab is selected
  useEffect(() => {
    if (activeTab === 'chat') {
      fetchChatMessages();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-switch to Chat tab during builds and start polling
  useEffect(() => {
    if (project?.status === 'building' || project?.status === 'self_fixing' || project?.status === 'waiting_for_user') {
      // Always switch to chat and start polling when building/self-fixing/waiting
      if (activeTab !== 'chat') {
        setActiveTab('chat');
      }
      setPipelineRunning(true);
      setFailedSince(null); // Reset failed timer when actively building
      fetchChatMessages();
      if (!pollRef.current) {
        startPolling();
      }
    } else if (project?.status === 'failed') {
      // Track when the project first became 'failed'
      setFailedSince(prev => prev || Date.now());
    } else {
      setFailedSince(null);
    }
  }, [project?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cycle vibe messages every 4 seconds during active builds
  useEffect(() => {
    if (project?.status === 'building' || project?.status === 'self_fixing' || pipelineRunning) {
      const interval = setInterval(() => setVibeTick(t => t + 1), 4000);
      return () => clearInterval(interval);
    }
  }, [project?.status, pipelineRunning]);


  // Auto-scroll chat on new messages — only when user is near the bottom
  useEffect(() => {
    if (chatEndRef.current && isNearBottomRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, streamingContent, pipelineRunning]);

  const fetchChatMessages = async () => {
    setChatLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${id}/conversations`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const msgs = data.messages || [];
        setChatMessages(msgs);
        // Determine pipeline state — project.status is the PRIMARY signal
        const projectStatus = project?.status;
        const isActivelyBuilding = projectStatus === 'building' || projectStatus === 'self_fixing' || projectStatus === 'running';
        
        if (isActivelyBuilding) {
          // If project is actively building, ALWAYS keep pipeline running — regardless of chat messages
          setPipelineRunning(true);
          if (!pollRef.current) startPolling();
        } else {
          // Only use chat messages as fallback when project status isn't 'building'
          const hasStart = msgs.some(m => m.step === 'start');
          const hasDone = msgs.some(m => m.step === 'done');
          const hasTerminalError = msgs.some(m => m.step === 'error' && (m.agent === 'Build Orchestrator' || m.agent === 'CodeFoundry'));
          if (hasStart && !hasDone && !hasTerminalError) {
            setPipelineRunning(true);
            startPolling();
          } else {
            setPipelineRunning(false);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load chat:', err);
    } finally {
      setChatLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) { alert('Image must be under 10MB'); continue; }
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: formData, credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setChatImages(prev => [...prev, { url: data.url, preview: URL.createObjectURL(file) }]);
        }
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePasteImage = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (!item.type.startsWith('image/')) continue;
      e.preventDefault();
      const file = item.getAsFile();
      if (!file || file.size > 10 * 1024 * 1024) continue;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: formData, credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setChatImages(prev => [...prev, { url: data.url, preview: URL.createObjectURL(file) }]);
        }
      } catch (err) {
        console.error('Paste upload failed:', err);
      }
    }
  };

  const handleDropImage = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) continue;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: formData, credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setChatImages(prev => [...prev, { url: data.url, preview: URL.createObjectURL(file) }]);
        }
      } catch (err) {
        console.error('Drop upload failed:', err);
      }
    }
  };

  const removeImage = (idx) => {
    setChatImages(prev => prev.filter((_, i) => i !== idx));
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() && chatImages.length === 0) return;
    setChatSending(true);
    const msgText = chatInput.trim();
    const imgUrls = chatImages.map(img => img.url);
    const currentMode = chatMode;
    
    // Optimistic UI: show user message immediately
    const tempUserMsg = { role: 'user', content: msgText, images: imgUrls, created_at: new Date().toISOString() };
    setChatMessages(prev => [...prev, tempUserMsg]);
    setChatInput('');
    clearDraft(id);
    setChatImages([]);
    
    try {
      // If project is building or waiting for user input, use the respond endpoint
      const isBuildActive = project?.status === 'building' || project?.status === 'waiting_for_user' || project?.status === 'self_fixing';
      if (isBuildActive) {
        const res = await fetch(`${API_URL}/api/projects/${id}/respond`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ message: msgText, action: 'modified' }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.messages_remaining !== undefined && data.messages_remaining <= 5) {
            setChatMessages(prev => [...prev, {
              role: 'assistant', agent: 'Build Orchestrator', step: 'warning',
              content: `**Context limit warning:** ${data.messages_remaining} messages remaining in this build thread. Start a new build to add more requirements.`,
              created_at: new Date().toISOString(),
            }]);
          }
          // Continue polling to pick up the agent's response
          if (!pollRef.current) startPolling();
        } else if (res.status === 429) {
          const errData = await res.json();
          setChatMessages(prev => [...prev, {
            role: 'assistant', agent: 'Build Orchestrator', step: 'error',
            content: `**Context limit reached.** ${errData.detail || 'Start a new build to continue adding requirements.'}`,
            created_at: new Date().toISOString(),
          }]);
        }
        setChatSending(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/projects/${id}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: msgText, images: imgUrls, mode: currentMode }),
      });

      if (currentMode === 'feature') {
        if (res.ok) {
          const data = await res.json();
          setPipelineRunning(true);
          setChatMessages(prev => {
            const withoutTemp = prev.slice(0, -1);
            return [...withoutTemp, data.user_message, data.ai_message];
          });
          startPolling();
        }
      } else {
        // Streaming chat mode - read SSE
        if (!res.ok) {
          // Backend returned an error — show it in chat
          let errMsg = 'Sorry, I could not process your message. Please try again.';
          try {
            const errData = await res.json();
            errMsg = errData.detail || errMsg;
          } catch {}
          setChatMessages(prev => [...prev, {
            role: 'assistant', content: errMsg,
            agent: 'CodeFoundry AI', step: 'error',
            created_at: new Date().toISOString(),
          }]);
          setChatSending(false);
          return;
        }
        setIsStreaming(true);
        setStreamingContent('');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let fullText = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const parsed = JSON.parse(line.slice(6));
                if (parsed.type === 'token') {
                  fullText += parsed.content;
                  setStreamingContent(fullText);
                } else if (parsed.type === 'done') {
                  // Stream complete - add final message to chat
                  const aiMsg = {
                    role: 'assistant',
                    content: parsed.content,
                    created_at: new Date().toISOString(),
                  };
                  setChatMessages(prev => [...prev, aiMsg]);
                  setStreamingContent('');
                  setIsStreaming(false);
                  // Refresh artifacts — code files may have been extracted from chat
                  try {
                    const artRes = await fetch(`${API_URL}/api/projects/${id}/artifacts`, { credentials: 'include' });
                    if (artRes.ok) {
                      const artData = await artRes.json();
                      setArtifacts(artData.artifacts || {});
                    }
                  } catch {}
                }
              } catch {}
            }
          }
        }
        setIsStreaming(false);
        setStreamingContent('');
      }
    } catch (err) {
      console.error('Send failed:', err);
      // Show error in chat so user knows what happened
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I couldn\'t process your message — please check your connection and try again.',
        agent: 'CodeFoundry AI', step: 'error',
        created_at: new Date().toISOString(),
      }]);
      setIsStreaming(false);
      setStreamingContent('');
    } finally {
      setChatSending(false);
    }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        // Fetch messages, project status, tool logs, AND artifacts
        const [msgRes, projRes, toolRes, artRes] = await Promise.all([
          fetch(`${API_URL}/api/projects/${id}/conversations`, { credentials: 'include' }),
          fetch(`${API_URL}/api/projects/${id}`, { credentials: 'include' }),
          fetch(`${API_URL}/api/projects/${id}/tool-logs`, { credentials: 'include' }),
          fetch(`${API_URL}/api/projects/${id}/artifacts`, { credentials: 'include' }),
        ]);
        if (msgRes.ok) {
          const data = await msgRes.json();
          const msgs = data.messages || [];
          setChatMessages(msgs);
        }
        if (toolRes.ok) {
          const data = await toolRes.json();
          setToolLogs(data.tool_logs || []);
        }
        if (artRes.ok) {
          const artData = await artRes.json();
          setArtifacts(artData.artifacts || {});
        }
        if (projRes.ok) {
          const projData = await projRes.json();
          setProject(projData);
          // Stop polling when build is no longer in progress
          const isDone = (projData.status === 'completed' || projData.status === 'failed' || projData.status === 'context_limit') && projData.status !== 'self_fixing' && projData.status !== 'waiting_for_user';
          if (isDone) {
            setPipelineRunning(false);
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch (err) { console.error('Poll error:', err); }
    }, 2000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <XCircle size={32} style={{ margin: '0 auto 0.75rem', color: '#ef4444' }} />
        <p style={{ color: '#ef4444', fontSize: '0.875rem', fontWeight: 500 }}>{error}</p>
        <Link to="/history" style={{ display: 'inline-block', marginTop: '1rem', color: 'hsl(var(--accent))', fontSize: '0.8125rem' }}>Back to History</Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
        <p style={{ marginTop: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Loading project...</p>
      </div>
    );
  }

  const currentIdx = PIPELINE_STEPS.findIndex(s => s.key === project.current_state);

  const getStepClass = (idx) => {
    const step = PIPELINE_STEPS[idx];
    // If project completed, all steps are done
    if (project.status === 'completed') {
      return 'done';
    }
    // Skip SELF_FIX step visually if it wasn't triggered
    if (step.key === 'SELF_FIX' && project.current_state === 'DONE') return 'done';
    if (step.key === 'SELF_FIX' && currentIdx < idx && project.status !== 'self_fixing') return '';
    // Context limit — mark all up to current as done, current as active (paused)
    if (project.status === 'context_limit') {
      if (idx < currentIdx) return 'done';
      if (idx === currentIdx) return 'active';
      return '';
    }
    // Building/running — current step is "active" (in progress), not failed
    if ((project.status === 'building' || project.status === 'running') && idx === currentIdx) return 'active';
    // Waiting state shows as active with special style
    if (project.status === 'waiting_for_user' && idx === currentIdx) return 'active';
    // Only show failed if project ACTUALLY failed (terminal state)
    if (project.status === 'failed' && idx === currentIdx) return 'failed';
    if (project.status === 'self_fixing' && step.key === 'SELF_FIX') return 'active';
    if (idx < currentIdx || project.current_state === 'DONE') return 'done';
    if (idx === currentIdx) return 'active';
    return '';
  };

  const getStatusBadge = () => {
    switch (project.status) {
      case 'completed': return <span className="badge badge-success" data-testid="project-detail-status"><CheckCircle size={12} /> Completed</span>;
      case 'running': return <span className="badge badge-info" data-testid="project-detail-status"><Loader size={12} /> Running</span>;
      case 'building': return <span className="badge badge-info" data-testid="project-detail-status"><Loader size={12} /> Building</span>;
      case 'failed': return <span className="badge badge-error" data-testid="project-detail-status"><XCircle size={12} /> Failed</span>;
      case 'self_fixing': return <span className="badge badge-warning" data-testid="project-detail-status"><Zap size={12} /> Self-Fixing</span>;
      case 'waiting_for_user': return <span className="badge badge-warning" data-testid="project-detail-status"><MessageCircle size={12} /> Waiting for You</span>;
      case 'context_limit': return <span className="badge badge-warning" data-testid="project-detail-status"><GitFork size={12} /> Fork to Continue</span>;
      default: return <span className="badge badge-warning" data-testid="project-detail-status"><Clock size={12} /> {project.status}</span>;
    }
  };

  const formatTime = (ts) => {
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return ''; }
  };

  const codeFiles = artifacts.code?.files || [];
  const currentFile = selectedFile !== null ? codeFiles[selectedFile] : (codeFiles[0] || null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabs = [
    { key: 'chat', label: 'Chat', icon: MessageCircle },
    { key: 'progress', label: 'Build', icon: Activity },
    { key: 'intelligence', label: 'Intelligence', icon: BarChart3 },
    { key: 'prd', label: 'PRD', icon: FileText },
    { key: 'architecture', label: 'Arch', icon: Code2 },
    { key: 'code', label: 'Code', icon: FileCode },
    { key: 'security', label: 'Security', icon: Shield },
    { key: 'versions', label: 'Versions', icon: History },
    { key: 'preview', label: 'Preview', icon: Eye },
    { key: 'git', label: 'Deploy', icon: Upload },
  ];

  const handleFork = async () => {
    if (!forkIdea.trim()) return;
    setForkLoading(true);
    
    // Open new tab IMMEDIATELY on user click (before async fetch)
    // Browsers block window.open inside async callbacks as popup
    const newTab = window.open('about:blank', '_blank');
    if (newTab) {
      newTab.document.title = 'CodeFoundry — Forking...';
      newTab.document.body.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100vh;background:#0f1219;color:#e2e8f0;font-family:sans-serif;"><div style="text-align:center"><h2 style="color:#818cf8">Forking project...</h2><p style="color:#94a3b8">Setting up your new build. Please wait.</p></div></div>';
    }
    
    try {
      const res = await fetch(`${API_URL}/api/projects/fork`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_project_id: id, new_idea: forkIdea.trim() }),
        credentials: 'include',
      });
      if (res.ok) {
        const forked = await res.json();
        refreshCredits();
        // Redirect the pre-opened tab to the forked project
        if (newTab && !newTab.closed) {
          newTab.location.href = `/project/${forked.id}`;
        }
        setForkIdea('');
      } else {
        // Fork failed — close the blank tab
        if (newTab && !newTab.closed) newTab.close();
        const err = await res.json().catch(() => ({}));
        if (res.status === 402) {
          alert(`Insufficient credits. Forks cost $10. ${err.detail || 'Purchase more credits to continue.'}`);
        } else {
          alert(err.detail || 'Fork failed. Please try again.');
        }
      }
    } catch (err) {
      // Error — close the blank tab
      if (newTab && !newTab.closed) newTab.close();
      console.error('Fork error:', err);
      alert('Fork failed due to a network error. Please try again.');
    } finally {
      setForkLoading(false);
    }
  };

  const checkSandboxStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${id}/sandbox/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSandboxStatus(data.status);
        if (data.sandbox) setSandboxInfo(data.sandbox);
      }
    } catch (err) {
      console.error('Sandbox status check failed:', err);
    }
  };

  const launchSandbox = async () => {
    setSandboxLoading(true);
    setSandboxLogs('');
    try {
      const res = await fetch(`${API_URL}/api/projects/${id}/sandbox/start`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({ status: 'error', message: 'Server error' }));
      if (res.ok && data.status === 'running') {
        setSandboxStatus('running');
        setSandboxInfo(data.sandbox);
        setPreviewKey(k => k + 1);
      } else {
        setSandboxStatus('error');
        setSandboxLogs(data.detail || data.message || 'Failed to start preview. Docker may not be available on this server.');
      }
    } catch (err) {
      setSandboxStatus('error');
      setSandboxLogs(err.message);
    } finally {
      setSandboxLoading(false);
    }
  };

  const stopSandbox = async () => {
    setSandboxLoading(true);
    try {
      await fetch(`${API_URL}/api/projects/${id}/sandbox/stop`, {
        method: 'POST',
        credentials: 'include',
      });
      setSandboxStatus('not_running');
      setSandboxInfo(null);
    } catch (err) {
      console.error('Stop sandbox failed:', err);
    } finally {
      setSandboxLoading(false);
    }
  };

  const fetchSandboxLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${id}/sandbox/logs`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSandboxLogs(data.logs || '');
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${id}/verify-status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVerification(data);
        return data;
      }
    } catch (e) { console.error('Verification status error:', e); }
    return null;
  };

  const triggerVerification = async () => {
    setVerifyLoading(true);
    setVerification({ status: 'running', gates: {}, push_ready: false });
    try {
      const res = await fetch(`${API_URL}/api/projects/${id}/verify`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        // Poll for status updates
        const poll = setInterval(async () => {
          const status = await fetchVerificationStatus();
          if (status && status.status !== 'running') {
            clearInterval(poll);
            setVerifyLoading(false);
          }
        }, 2000);
        // Safety timeout
        setTimeout(() => { clearInterval(poll); setVerifyLoading(false); }, 300000);
      } else {
        const err = await res.json().catch(() => ({}));
        setVerification({ status: 'error', error: err.detail || 'Failed to start verification', gates: {}, push_ready: false });
        setVerifyLoading(false);
      }
    } catch (e) {
      setVerification({ status: 'error', error: e.message, gates: {}, push_ready: false });
      setVerifyLoading(false);
    }
  };

  const fetchGitHubStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/github`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setGithubStatus(data);
        if (data.connected) {
          // Load orgs
          const orgsRes = await fetch(`${API_URL}/api/github/orgs`, { credentials: 'include' });
          if (orgsRes.ok) {
            const orgsData = await orgsRes.json();
            setOrgs(orgsData);
            if (orgsData.length > 0) {
              setSelectedOrg(orgsData[0].login);
              fetchRepos(orgsData[0].login);
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch GitHub status:', err);
    }
  };

  const fetchRepos = async (owner) => {
    try {
      const res = await fetch(`${API_URL}/api/github/repos?owner=${owner}&search=${repoSearch}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRepos(data);
      }
    } catch (err) {
      console.error('Failed to fetch repos:', err);
    }
  };

  const fetchBranches = async (repoFullName) => {
    try {
      const res = await fetch(`${API_URL}/api/github/branches?repo=${repoFullName}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBranches(data);
        if (data.length > 0) {
          setSelectedBranch(data[0].name);
        } else {
          // Default to main if no branches returned (new repo)
          setBranches([{ name: 'main' }]);
          setSelectedBranch('main');
        }
      }
    } catch (err) {
      setBranches([{ name: 'main' }]);
      setSelectedBranch('main');
    }
  };

  const createNewRepo = async () => {
    if (!newRepoName.trim()) return;
    setCreatingRepo(true);
    setPushError('');
    try {
      const res = await fetch(`${API_URL}/api/github/repos/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newRepoName.trim(), owner: selectedOrg, private: isPrivateRepo }),
      });
      const data = await res.json();
      if (res.ok) {
        setSelectedRepo(data);
        setShowNewRepo(false);
        setNewRepoName('');
        // Delay fetch to allow GitHub API to propagate the new repo
        setTimeout(() => fetchRepos(selectedOrg), 1500);
        fetchBranches(data.full_name);
      } else {
        setPushError(data.detail || 'Failed to create repository. Check your GitHub permissions.');
      }
    } catch (err) {
      console.error('GitHub repo create error:', err);
      setPushError(err?.message || 'Failed to connect to server. Please check your connection and try again.');
    } finally {
      setCreatingRepo(false);
    }
  };

  const pushToGitHub = async () => {
    if (!selectedRepo) {
      setPushError('Please select a repository');
      return;
    }
    setPushLoading(true);
    setPushError('');
    setPushResult(null);
    try {
      const res = await fetch(`${API_URL}/api/projects/${id}/push-to-github`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          repo_full_name: selectedRepo.full_name,
          branch: selectedBranch,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setPushResult(data);
        try {
          const artRes = await fetch(`${API_URL}/api/projects/${id}/artifacts`, { credentials: 'include' });
          if (artRes.ok) {
            const artData = await artRes.json();
            setArtifacts(artData.artifacts || {});
          }
        } catch (artErr) { console.error('Artifact refresh error:', artErr); }
      } else {
        setPushError(data.detail || 'Failed to push to GitHub');
      }
    } catch (err) {
      console.error('GitHub push error:', err);
      setPushError(err?.message || 'Failed to push to GitHub. Please try again.');
    } finally {
      setPushLoading(false);
    }
  };

  const previewUrl = `${API_URL}/api/projects/${id}/preview`;

  // Compute build state at component level for use in both banner and chat input
  const isActiveBuild = project?.status === 'building' || project?.status === 'running' || project?.status === 'self_fixing' || pipelineRunning;
  const failedLongEnough = failedSince && (Date.now() - failedSince > 15000);
  const showAgentActive = isActiveBuild || (project?.status === 'failed' && !failedLongEnough) || project?.status === 'waiting_for_user';
  const isBuildDisabled = isActiveBuild || (showAgentActive && project?.status !== 'waiting_for_user');

  return (
    <div data-testid="project-detail-page" style={{ padding: '0 1.5rem', maxWidth: '100%' }}>
      {/* Back Link + Project Name — Minimal Top Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '1rem',
        padding: '0.75rem 0', marginBottom: '0.5rem',
        borderBottom: '1px solid hsl(var(--border))',
        position: 'sticky', top: 0, zIndex: 20,
        background: 'hsl(var(--background))',
      }}>
        <Link to="/history" data-testid="back-to-projects" style={{
          textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.375rem',
          color: '#818cf8', fontSize: '0.875rem', fontWeight: 600, flexShrink: 0,
          transition: 'color 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#a5b4fc'}
        onMouseLeave={e => e.currentTarget.style.color = '#818cf8'}
        >
          <ArrowLeft size={16} /> Back to Projects
        </Link>
        <div style={{ width: '1px', height: '20px', background: 'hsl(var(--border))' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-testid="project-detail-title">{(project.name || '').replace(/\s*\(Fork\)/gi, '').replace(/^Build\s+A\s+/i, '')}</h1>
        </div>
      </div>

      {/* Active Agent Banner — always visible to show what agent is/was doing */}
      {project && project.status !== 'idle' && (() => {
        const lastAgentMsg = [...chatMessages].reverse().find(m => m.role === 'assistant' && m.agent);
        const activeAgent = lastAgentMsg?.agent || 'Build Orchestrator';
        const agentTheme = AGENT_COLORS[activeAgent] || AGENT_COLORS.default;
        const AgentIcon = AGENT_ICON_MAP[activeAgent] || Bot;
        const isWaiting = project?.status === 'waiting_for_user';
        const isBuilding = project?.status === 'building' || project?.status === 'running' || project?.status === 'self_fixing' || pipelineRunning;
        // Only show "Build Failed" if status has been 'failed' for more than 15 seconds (prevents flicker during step transitions)
        const failedLongEnough = failedSince && (Date.now() - failedSince > 15000);
        const isFailed = project?.status === 'failed' && failedLongEnough && !pipelineRunning && !chatSending && !isStreaming && !isBuilding;
        // Show dots when building OR when status is transitioning (not yet confirmed failed)
        const showAgentActive = isBuilding || (project?.status === 'failed' && !failedLongEnough) || project?.status === 'waiting_for_user';
        const isCompleted = project?.status === 'completed';
        const isContextLimit = project?.status === 'context_limit';
        
        // Stage-specific vibe messages — cycle through for each pipeline stage
        const STAGE_VIBES = {
          'IDEA': [
            'Warming things up', 'Getting started', 'Kicking things off',
            'Starting the engine', 'Setting things in motion', 'Launching sequence',
          ],
          'INTAKE': [
            'Analyzing your requirements', 'Understanding intent', 'Processing context',
            'Interpreting signals', 'Evaluating options', 'Thinking through the task',
          ],
          'PLANNING': [
            'Planning execution', 'Reasoning step-by-step', 'Generating insights',
            'Breaking down features', 'Orchestrating intelligent flow', 'Formulating response',
          ],
          'PRD': [
            'Writing product requirements', 'Analyzing inputs', 'Processing context',
            'Driving agent decisions', 'Executing adaptive logic', 'Sharpening details',
          ],
          'ARCH': [
            'Designing system architecture', 'Connecting modules', 'Assembling pieces',
            'Wiring things together', 'Coordinating services', 'Unifying processes',
          ],
          'DATABASE': [
            'Designing database schema', 'Binding dependencies', 'Linking components',
            'Syncing components', 'Structuring data models', 'Optimizing flow',
          ],
          'API_DESIGN': [
            'Designing API endpoints', 'Merging workflows', 'Integrating systems',
            'Coordinating services', 'Aligning components', 'Wiring things together',
          ],
          'UIUX_DESIGN': [
            'Creating UI/UX specifications', 'Fine-tuning components', 'Polishing logic',
            'Refining outputs', 'Sharpening details', 'Elevating quality',
          ],
          'CODE': [
            'Generating production code', 'Executing steps', 'Doing the heavy lifting',
            'Running the process', 'Applying logic', 'Performing operations',
            'Driving execution', 'Advancing workflow', 'Powering intelligent execution',
          ],
          'CODE_REVIEW': [
            'Reviewing code quality', 'Validating results', 'Running checks',
            'Inspecting data', 'Confirming accuracy', 'Checking integrity',
            'Ensuring consistency', 'Double-checking everything', 'Testing outcomes',
          ],
          'INTEGRATION': [
            'Setting up integrations', 'Connecting modules', 'Linking components',
            'Merging workflows', 'Syncing components', 'Enabling seamless automation',
          ],
          'TEST': [
            'Writing test suites', 'Verifying outputs', 'Running checks',
            'Testing outcomes', 'Confirming accuracy', 'Ensuring consistency',
          ],
          'SECURITY': [
            'Running security analysis', 'Checking integrity', 'Inspecting data',
            'Validating results', 'Strengthening output', 'Reducing friction',
          ],
          'DOCS': [
            'Generating documentation', 'Wrapping things up', 'Finishing touches',
            'Polishing logic', 'Refining outputs', 'Completing process',
          ],
          'DEVOPS': [
            'Configuring deployment', 'Finalizing setup', 'Locking everything in',
            'Sealing the results', 'Activating smart pipelines', 'Streamlining flow',
          ],
          'MONITOR': [
            'Setting up monitoring', 'Running in the background', 'Monitoring progress',
            'Keeping things updated', 'Watching for changes', 'Staying on top of things',
          ],
          'SELF_FIX': [
            'Auto-fixing detected issues', 'Adjusting parameters', 'Tweaking the setup',
            'Tightening configurations', 'Calibrating systems', 'Improving performance',
          ],
          'DONE': [
            'All set', 'Done and ready', 'Wrapping things up',
            'Marking as complete', 'Closing the loop', 'Delivering optimized outcomes',
          ],
        };
        // Cycle through vibes using tick counter (updated every 4 seconds)
        const currentVibes = STAGE_VIBES[project?.current_state] || ['Processing'];
        const stageDesc = currentVibes[vibeTick % currentVibes.length];
        
        // File progress for CODE state
        const codeFilesMade = chatMessages.filter(m => (m.step === 'file_complete' || m.step === 'code_progress') && m.agent === 'Coding Agent').length;
        const lastFileMade = [...chatMessages].reverse().find(m => (m.step === 'file_complete' || m.step === 'code_progress') && m.agent === 'Coding Agent');
        const lastFileName = lastFileMade?.content?.match(/`([^`]+)`/)?.[1] || '';
        
        let progressText = stageDesc;
        if (project?.current_state === 'CODE' && codeFilesMade > 0) {
          progressText = lastFileName
            ? `Working on ${lastFileName.split('/').pop()} — ${codeFilesMade} file${codeFilesMade !== 1 ? 's' : ''} generated`
            : `${codeFilesMade} file${codeFilesMade !== 1 ? 's' : ''} generated so far`;
        }
        
        return (
          <div data-testid="active-agent-banner" style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '0.75rem 1rem', marginBottom: '0.75rem',
            background: isFailed ? 'rgba(239,68,68,0.06)'
              : isCompleted ? 'rgba(16,185,129,0.06)'
              : isContextLimit ? 'rgba(99,102,241,0.06)'
              : isWaiting ? 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(251,191,36,0.04))'
              : `${agentTheme.accent}08`,
            border: isFailed ? '1px solid rgba(239,68,68,0.25)'
              : isCompleted ? '1px solid rgba(16,185,129,0.25)'
              : isContextLimit ? '1px solid rgba(99,102,241,0.25)'
              : isWaiting ? '1px solid rgba(245,158,11,0.3)'
              : `1px solid ${agentTheme.accent}20`,
            borderRadius: '0.75rem',
          }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '0.5rem', flexShrink: 0,
              background: isFailed ? 'rgba(239,68,68,0.12)'
                : isCompleted ? 'rgba(16,185,129,0.12)'
                : isContextLimit ? 'rgba(99,102,241,0.12)'
                : isWaiting ? 'rgba(245,158,11,0.12)'
                : agentTheme.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: isFailed ? '0 0 0 2px rgba(239,68,68,0.2)'
                : isCompleted ? '0 0 0 2px rgba(16,185,129,0.2)'
                : isWaiting ? '0 0 0 2px rgba(245,158,11,0.25)'
                : `0 0 0 2px ${agentTheme.ring}`,
              animation: (isBuilding || showAgentActive) ? 'step-pulse 1.8s ease-in-out infinite' : 'none',
            }}>
              {isFailed ? <XCircle size={18} style={{ color: '#ef4444' }} />
                : isCompleted ? <CheckCircle size={18} style={{ color: '#10b981' }} />
                : isContextLimit ? <GitFork size={18} style={{ color: '#6366f1' }} />
                : isWaiting ? <MessageCircle size={18} style={{ color: '#f59e0b' }} />
                : <AgentIcon size={18} style={{ color: agentTheme.accent, animation: 'spin 3s linear infinite' }} />
              }
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem',
                color: isFailed ? '#ef4444' : isCompleted ? '#10b981' : isContextLimit ? '#6366f1' : isWaiting ? '#f59e0b' : agentTheme.accent,
              }}>
                {isFailed ? 'Build Failed'
                  : isCompleted ? 'Build Complete'
                  : isContextLimit ? 'Context Limit Reached'
                  : isWaiting ? 'Your Response Needed'
                  : activeAgent
                }
                {(isBuilding || showAgentActive) && (
                  <span style={{ display: 'inline-flex', gap: '3px', marginLeft: '0.125rem' }}>
                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: agentTheme.accent, animation: 'bounce 1.4s infinite', animationDelay: '0s' }} />
                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: agentTheme.accent, animation: 'bounce 1.4s infinite', animationDelay: '0.2s' }} />
                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: agentTheme.accent, animation: 'bounce 1.4s infinite', animationDelay: '0.4s' }} />
                  </span>
                )}
                {isBuilding && codeFilesMade > 0 && project?.current_state === 'CODE' && (
                  <span style={{
                    fontSize: '0.6875rem', fontWeight: 700, color: '#10b981',
                    background: 'rgba(16,185,129,0.12)', padding: '0.125rem 0.5rem',
                    borderRadius: '9999px', fontFamily: '"JetBrains Mono", monospace',
                  }}>
                    {codeFilesMade} files
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#a1a9b8', marginTop: '0.125rem' }}>
                {isFailed ? <>{activeAgent} stopped at <strong>{project?.current_state}</strong> — retry or fork to continue</>
                  : isCompleted ? <>All agents finished successfully — your code is ready</>
                  : isContextLimit ? <>Fork this project to continue building from where it stopped</>
                  : isWaiting ? <>{activeAgent} is waiting for your input — scroll down</>
                  : <>{progressText}</>
                }
              </div>
            </div>
            <div style={{ fontSize: '0.6875rem', fontFamily: '"JetBrains Mono", monospace', opacity: 0.7,
              color: isFailed ? '#ef4444' : isCompleted ? '#10b981' : isContextLimit ? '#6366f1' : agentTheme.accent,
            }}>
              {project?.current_state || ''}
            </div>
          </div>
        );
      })()}

      {/* Build Success — Analytics Card + Social Sharing + Referral */}
      {project.status === 'completed' && buildAnalytics && (
        <div data-testid="build-success-card" style={{
          marginBottom: '1rem', padding: '1.25rem', borderRadius: '0.75rem',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(16,185,129,0.08))',
          border: '1px solid rgba(99,102,241,0.2)',
        }}>
          {/* CodeFoundry Hologram Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '0.75rem',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 20px rgba(99,102,241,0.4), 0 0 40px rgba(99,102,241,0.1)',
              animation: 'agent-pulse 2s ease-in-out infinite',
            }}>
              <Trophy size={22} style={{ color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#c7d2fe' }}>Build Complete!</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{buildAnalytics.project_name}</div>
            </div>
          </div>

          {/* Analytics Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            {[
              { label: 'Files', value: buildAnalytics.total_files, icon: FileCode, color: '#6366f1' },
              { label: 'Lines of Code', value: `${(buildAnalytics.total_lines || 0).toLocaleString()}`, icon: Code2, color: '#8b5cf6' },
              { label: 'Build Time', value: buildAnalytics.build_time_display, icon: Clock, color: '#f59e0b' },
              { label: 'Confidence', value: `${buildAnalytics.confidence_score}%`, icon: Shield, color: '#10b981' },
            ].map((stat, i) => (
              <div key={i} style={{
                padding: '0.625rem', borderRadius: '0.5rem', background: 'rgba(0,0,0,0.15)',
                textAlign: 'center',
              }}>
                <stat.icon size={16} style={{ color: stat.color, margin: '0 auto 0.25rem' }} />
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#f8fafc' }}>{stat.value}</div>
                <div style={{ fontSize: '0.625rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Social Share Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600 }}>Share your achievement:</span>
            <button
              data-testid="share-x-btn"
              onClick={() => {
                const text = encodeURIComponent(buildAnalytics.share_text || `Built ${buildAnalytics.project_name} with @CodeFoundrySH!`);
                window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.375rem 0.75rem', borderRadius: '0.375rem',
                background: '#1d9bf0', color: '#fff', border: 'none',
                fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              <ExternalLink size={12} /> Share on X
            </button>
            <button
              data-testid="share-linkedin-btn"
              onClick={() => {
                const text = encodeURIComponent(buildAnalytics.linkedin_text || buildAnalytics.share_text || '');
                window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${text}`, '_blank');
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.375rem 0.75rem', borderRadius: '0.375rem',
                background: '#0a66c2', color: '#fff', border: 'none',
                fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              <ExternalLink size={12} /> Share on LinkedIn
            </button>
          </div>

          {/* Referral Link */}
          {referralData && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem',
              background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)',
              borderRadius: '0.375rem', flexWrap: 'wrap',
            }}>
              <Gift size={14} style={{ color: '#a78bfa' }} />
              <span style={{ fontSize: '0.75rem', color: '#c7d2fe' }}>Refer friends &amp; both get 10 credits!</span>
              <code data-testid="referral-code-display" style={{
                fontSize: '0.75rem', fontFamily: '"JetBrains Mono", monospace',
                background: 'rgba(0,0,0,0.2)', padding: '0.125rem 0.5rem', borderRadius: '0.25rem',
                color: '#a78bfa',
              }}>
                {referralData.code}
              </code>
              <button
                data-testid="copy-referral-btn"
                onClick={() => { navigator.clipboard.writeText(referralData.url); }}
                style={{
                  padding: '0.25rem 0.5rem', borderRadius: '0.25rem',
                  background: '#8b5cf6', color: '#fff', border: 'none',
                  fontSize: '0.6875rem', cursor: 'pointer',
                }}
              >
                <Copy size={10} /> Copy Link
              </button>
            </div>
          )}
        </div>
      )}

      {/* Continue Build Banner (shown when build TRULY failed — status='failed' for 15+ seconds AND no active work) */}
      {project.status === 'failed' && failedLongEnough && !pipelineRunning && !chatSending && !isStreaming && project.status !== 'building' && project.status !== 'self_fixing' && (
        <div data-testid="continue-build-banner" style={{ marginBottom: '1rem', padding: '1rem 1.25rem', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#f59e0b' }}>Build failed at: {project.current_state || 'unknown'}</p>
            <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
              Each self-fix attempt costs <strong>$20</strong> in credits. It typically takes 2-3 builds to reach production-ready code.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              data-testid="self-fix-btn"
              onClick={async () => {
                try {
                  const res = await fetch(`${API_URL}/api/projects/${id}/self-fix`, {
                    method: 'POST', credentials: 'include',
                  });
                  const data = await res.json();
                  if (res.ok) {
                    setActiveTab('chat');
                    setPipelineRunning(true);
                    startPolling();
                    fetchChatMessages();
                  } else if (res.status === 402) {
                    alert(data.detail || 'Insufficient credits. Purchase more credits to continue.');
                  } else {
                    alert(data.detail || 'Self-fix failed. Try Continue Build instead.');
                  }
                } catch (err) {
                  console.error('Self-fix trigger failed:', err);
                  alert('Network error. Please try again.');
                }
              }}
              style={{ padding: '0.5rem 1.25rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
            >
              <Zap size={14} /> Self-Fix ($20/attempt)
            </button>
            <button
              data-testid="continue-build-btn"
              onClick={async () => {
                try {
                  const res = await fetch(`${API_URL}/api/projects/${id}/retry`, {
                    method: 'POST', credentials: 'include',
                  });
                  const data = await res.json();
                  if (res.ok) {
                    setActiveTab('chat');
                    setPipelineRunning(true);
                    startPolling();
                    fetchChatMessages();
                    window.location.reload();
                  } else {
                    alert(data.detail || 'Failed to continue build.');
                  }
                } catch (err) {
                  console.error('Continue build failed:', err);
                  alert('Network error. Please try again.');
                }
              }}
              style={{ padding: '0.5rem 1.25rem', background: '#f59e0b', color: '#000', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
            >
              Continue Build
            </button>
          </div>
        </div>
      )}

      {/* Self-Fixing Banner */}
      {project.status === 'self_fixing' && (
        <div data-testid="self-fix-banner" style={{
          marginBottom: '1rem', padding: '1rem 1.25rem',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(168,85,247,0.08))',
          border: '1px solid rgba(99,102,241,0.25)', borderRadius: '0.75rem',
          display: 'flex', alignItems: 'center', gap: '1rem',
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '0.5rem', flexShrink: 0,
            background: 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Zap size={18} style={{ color: '#6366f1', animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
          <div>
            <p style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#6366f1' }}>Self-Fix in Progress — $20/attempt</p>
            <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>The agent is analyzing errors and applying fixes. Credits are charged per attempt. Runs until all errors are resolved or credits are exhausted.</p>
          </div>
        </div>
      )}

      {/* Context Limit Banner — Fork to Continue */}
      {project.status === 'context_limit' && (
        <div data-testid="context-limit-banner" style={{
          marginBottom: '1rem', padding: '1rem 1.25rem',
          background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(234,179,8,0.08))',
          border: '1px solid rgba(245,158,11,0.25)', borderRadius: '0.75rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <p style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <AlertTriangle size={16} /> Context Limit Reached (30 messages)
              </p>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                This build has reached its message limit. Fork the project to continue with fresh context.
                Forking costs <strong>$10</strong> and carries all artifacts from this build.
              </p>
            </div>
          </div>
          {/* Inline Fork Form */}
          <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'hsl(var(--background))', borderRadius: '0.5rem', border: '1px solid hsl(var(--border))' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', display: 'block', marginBottom: '0.375rem' }}>
              What would you like to change or continue building?
            </label>
            <textarea
              className="input"
              placeholder="e.g., Continue from where it stopped, add authentication, fix the API routes..."
              value={forkIdea}
              onChange={(e) => setForkIdea(e.target.value)}
              rows={2}
              style={{ marginBottom: '0.5rem', fontSize: '0.8125rem' }}
              data-testid="context-limit-fork-idea"
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                data-testid="context-limit-fork-btn"
                className="btn btn-primary"
                disabled={forkLoading || !forkIdea.trim()}
                onClick={handleFork}
                style={{ fontSize: '0.8125rem' }}
              >
                {forkLoading ? <><div className="spinner" /> Forking...</> : <><GitFork size={14} /> Fork & Continue ($10)</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credits Needed Banner — shown when user needs to add credits */}
      {project.status === 'waiting_for_user' && project.waiting_step === 'credits_needed' && (
        <div data-testid="credits-needed-banner" style={{
          marginBottom: '1rem', padding: '1rem 1.25rem',
          background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.08))',
          border: '1px solid rgba(99,102,241,0.25)', borderRadius: '0.75rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <p style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#6366f1', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <CreditCard size={16} /> Credits Required to Continue
              </p>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                Your build requires more credits to continue code generation. Add credits to resume.
              </p>
            </div>
            <button
              data-testid="credits-needed-buy-btn"
              className="btn btn-primary"
              onClick={() => window.location.href = '/buy-credits'}
              style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
            >
              <CreditCard size={14} /> Buy Credits
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tab-list" data-testid="project-detail-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab-trigger ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon size={14} style={{ display: 'inline', marginRight: '0.375rem', verticalAlign: '-2px' }} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ marginTop: '1.25rem' }}>

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <div className="log-stream" ref={logRef} data-testid="build-log-stream">
            {logs.length === 0 ? (
              <p style={{ color: 'hsl(var(--muted-foreground))', textAlign: 'center', padding: '2rem' }}>Waiting for logs...</p>
            ) : (
              logs.map((log, i) => (
                <div key={i} className="log-line">
                  <span className="log-time">{formatTime(log.timestamp)}</span>
                  <span className={`log-${log.level || 'info'}`}>{log.message}</span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Build Intelligence Tab */}
        {activeTab === 'intelligence' && (
          <div data-testid="intelligence-tab">
            {buildIntel ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Complexity Score Card */}
                <div className="card" data-testid="complexity-card">
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Gauge size={16} /> Build Complexity
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
                    <div style={{ padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: buildIntel.complexity?.level === 'High' ? '#ef4444' : buildIntel.complexity?.level === 'Medium' ? '#f59e0b' : '#10b981' }}>
                        {buildIntel.complexity?.score || 0}
                      </div>
                      <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>Score</div>
                    </div>
                    <div style={{ padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{buildIntel.complexity?.total_files || 0}</div>
                      <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>Files</div>
                    </div>
                    <div style={{ padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{buildIntel.complexity?.total_lines || 0}</div>
                      <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>Lines</div>
                    </div>
                    <div style={{ padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{buildIntel.complexity?.api_endpoints || 0}</div>
                      <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>APIs</div>
                    </div>
                    <div style={{ padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{buildIntel.complexity?.db_collections || 0}</div>
                      <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>Collections</div>
                    </div>
                    <div style={{ padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', textAlign: 'center' }}>
                      <div style={{
                        fontSize: '0.8125rem', fontWeight: 700, padding: '0.25rem 0.5rem', borderRadius: '999px', display: 'inline-block',
                        background: buildIntel.complexity?.level === 'High' ? '#ef444420' : buildIntel.complexity?.level === 'Medium' ? '#f59e0b20' : '#10b98120',
                        color: buildIntel.complexity?.level === 'High' ? '#ef4444' : buildIntel.complexity?.level === 'Medium' ? '#f59e0b' : '#10b981',
                      }}>
                        {buildIntel.complexity?.level || 'N/A'}
                      </div>
                      <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>Complexity</div>
                    </div>
                  </div>
                </div>

                {/* Agent Execution Panel */}
                <div className="card" data-testid="agent-visibility-card">
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Layers size={16} /> Agent Isolation & Execution
                  </h4>
                  {buildIntel.invocations && buildIntel.invocations.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {buildIntel.invocations.map((inv, i) => (
                        <div key={i} style={{
                          display: 'grid', gridTemplateColumns: '1fr auto auto auto auto',
                          gap: '0.75rem', alignItems: 'center', padding: '0.625rem 0.75rem',
                          background: 'hsl(var(--secondary))', borderRadius: '0.5rem', fontSize: '0.75rem',
                        }}>
                          <div style={{ fontWeight: 600 }}>{inv.agent_name?.replace('Agent', '') || 'Unknown'}</div>
                          <div style={{ color: 'hsl(var(--muted-foreground))' }}>{inv.duration_ms?.toFixed(0) || 0}ms</div>
                          <div style={{ color: 'hsl(var(--muted-foreground))' }}>Retry: {inv.retry_count || 0}</div>
                          <div style={{ color: inv.output_valid ? '#10b981' : '#ef4444' }}>{inv.output_valid ? 'Valid' : 'Invalid'}</div>
                          <span className={`badge ${inv.status === 'success' ? 'badge-success' : 'badge-error'}`}>{inv.status}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {['Product', 'Architect', 'Coding', 'Testing', 'Security', 'DevOps', 'Monitoring'].map((agent, i) => (
                        <div key={i} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '0.625rem 0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', fontSize: '0.75rem',
                        }}>
                          <div style={{ fontWeight: 600 }}>{agent}</div>
                          <span className="badge badge-success">Completed</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {buildIntel.build_summary && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid hsl(var(--border))' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 700 }}>{buildIntel.build_summary.total_invocations || 7}</div>
                        <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>Invocations</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 700 }}>{buildIntel.elapsed_seconds?.toFixed(1) || '0'}s</div>
                        <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>Duration</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 700 }}>${buildIntel.complexity?.estimated_cost?.toFixed(4) || '0'}</div>
                        <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>Cost</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 700 }}>{(buildIntel.build_summary.guard_violations || []).length}</div>
                        <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>Violations</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Test Intelligence Panel */}
                <div className="card" data-testid="test-intelligence-card">
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Zap size={16} /> Test Intelligence
                  </h4>
                  {buildIntel.test_report ? (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <div style={{ padding: '0.625rem', background: '#10b98115', borderRadius: '0.5rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>{buildIntel.test_report.coverage || buildIntel.test_report.coverage_label || 'N/A'}</div>
                          <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>Coverage</div>
                        </div>
                        <div style={{ padding: '0.625rem', background: '#10b98115', borderRadius: '0.5rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>{buildIntel.test_report.passed || 0}</div>
                          <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>Passed</div>
                        </div>
                        <div style={{ padding: '0.625rem', background: buildIntel.test_report.failed > 0 ? '#ef444415' : '#10b98115', borderRadius: '0.5rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: buildIntel.test_report.failed > 0 ? '#ef4444' : '#10b981' }}>{buildIntel.test_report.failed || 0}</div>
                          <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>Failed</div>
                        </div>
                        <div style={{ padding: '0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{buildIntel.test_report.total_files || 0}</div>
                          <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>Files</div>
                        </div>
                      </div>
                      {buildIntel.test_report.test_suites && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                          {Object.entries(buildIntel.test_report.test_suites).map(([suite, data]) => (
                            <div key={suite} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', fontSize: '0.75rem' }}>
                              <span style={{ textTransform: 'capitalize' }}>{suite.replace(/_/g, ' ')}</span>
                              <span style={{ color: data.status === 'pass' ? '#10b981' : '#f59e0b' }}>{data.passed}/{data.total} passed</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>Test results not available yet</p>}
                </div>
              </div>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>
                {project?.status === 'building' ? (
                  <><Loader size={20} style={{ margin: '0 auto 0.5rem', animation: 'spin 1s linear infinite' }} /><p style={{ fontSize: '0.875rem' }}>Build in progress... Intelligence data will appear once complete.</p></>
                ) : (
                  <p style={{ fontSize: '0.875rem' }}>Build intelligence not available yet</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* PRD Tab */}
        {activeTab === 'prd' && (
          <div className="card">
            {artifacts.prd ? (
              <div>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>{artifacts.prd.product_name || 'Product Requirements'}</h3>
                {artifacts.prd.description && <p style={{ marginBottom: '1rem', color: 'hsl(var(--muted-foreground))' }}>{artifacts.prd.description}</p>}
                
                {artifacts.prd.target_users && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Target users</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {artifacts.prd.target_users.map((u, i) => <span key={i} className="badge badge-info">{typeof u === 'string' ? u : u.name || JSON.stringify(u)}</span>)}
                    </div>
                  </div>
                )}

                {artifacts.prd.core_features && (
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Core features</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {artifacts.prd.core_features.map((f, i) => (
                        <div key={i} style={{ padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem' }}>
                          <div style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{f.name || f}</div>
                          {f.description && <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>{f.description}</div>}
                          {f.priority && <span className={`badge ${f.priority === 'high' ? 'badge-error' : 'badge-warning'}`} style={{ marginTop: '0.375rem' }}>{f.priority}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {artifacts.prd.tech_stack_suggestions && (
                  <div>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Suggested tech stack</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {artifacts.prd.tech_stack_suggestions.map((t, i) => <span key={i} className="chip">{t}</span>)}
                    </div>
                  </div>
                )}
              </div>
            ) : <p style={{ color: 'hsl(var(--muted-foreground))' }}>PRD not generated yet</p>}
          </div>
        )}

        {/* Architecture Tab */}
        {activeTab === 'architecture' && (
          <div className="card">
            {artifacts.architecture ? (
              <div>
                {artifacts.architecture.stack && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Tech stack</h4>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {Object.entries(artifacts.architecture.stack).map(([k, v]) => (
                        <div key={k} style={{ padding: '0.625rem 1rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem' }}>
                          <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{k}</div>
                          <div style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {artifacts.architecture.apis && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>API endpoints ({artifacts.architecture.apis.length})</h4>
                    <div className="code-block">
                      {artifacts.architecture.apis.map((api, i) => (
                        <div key={i} style={{ padding: '0.25rem 0' }}>
                          <span style={{ color: 'hsl(var(--accent))', fontWeight: 600 }}>{api.method}</span>{' '}
                          <span>{api.endpoint}</span>{' '}
                          <span style={{ color: 'hsl(var(--muted-foreground))' }}>— {api.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {artifacts.architecture.database_schema && (
                  <div>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Database schema</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.75rem' }}>
                      {artifacts.architecture.database_schema.map((col, i) => (
                        <div key={i} style={{ padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem' }}>
                          <div style={{ fontWeight: 600, fontSize: '0.8125rem', marginBottom: '0.375rem' }}>{col.collection}</div>
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                            {(col.fields || []).join(', ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : <p style={{ color: 'hsl(var(--muted-foreground))' }}>Architecture not generated yet</p>}
          </div>
        )}

        {/* Code Tab */}
        {activeTab === 'code' && (
          <div>
            {/* Build Confidence Index Banner */}
            {artifacts.build_confidence && (
              <div className="card" style={{ padding: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }} data-testid="build-confidence-banner">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '3rem', height: '3rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '1.125rem', letterSpacing: '-0.02em',
                    background: artifacts.build_confidence.index >= 90 ? 'rgba(16,185,129,0.15)' : artifacts.build_confidence.index >= 70 ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                    color: artifacts.build_confidence.index >= 90 ? '#10b981' : artifacts.build_confidence.index >= 70 ? '#f59e0b' : '#ef4444',
                  }}>
                    {artifacts.build_confidence.grade}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 700 }}>Build Confidence Index</div>
                    <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>{artifacts.build_confidence.files_analyzed} files analyzed</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.75rem' }}>
                  {[
                    ['Syntax', artifacts.build_confidence.avg_syntax],
                    ['Completeness', artifacts.build_confidence.avg_completeness],
                    ['Security', artifacts.build_confidence.avg_security],
                  ].map(([label, val]) => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 700, color: val >= 90 ? '#10b981' : val >= 70 ? '#f59e0b' : '#ef4444' }}>{val}%</div>
                      <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.625rem' }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '1rem' }}>
            {/* File tree */}
            <div className="card" style={{ padding: '0.5rem' }}>
              <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Files ({codeFiles.length})</div>
              {codeFiles.map((f, i) => (
                <div
                  key={i}
                  className={`file-tree-item ${selectedFile === i || (selectedFile === null && i === 0) ? 'active' : ''}`}
                  onClick={() => setSelectedFile(i)}
                  data-testid={`file-tree-item-${i}`}
                >
                  <FileCode size={14} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.path}</span>
                </div>
              ))}
              {codeFiles.length === 0 && <p style={{ padding: '1rem', fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>No files generated yet</p>}
            </div>

            {/* Code viewer */}
            <div>
              {currentFile ? (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, fontFamily: 'IBM Plex Mono, monospace' }}>{currentFile.path}</span>
                    <button
                      className="btn btn-ghost"
                      onClick={() => copyToClipboard(currentFile.content)}
                      style={{ fontSize: '0.75rem', padding: '0.375rem 0.625rem' }}
                      data-testid="code-preview-copy-button"
                    >
                      <Copy size={14} />
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="code-block" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <code>{currentFile.content}</code>
                  </pre>
                </div>
              ) : <p style={{ color: 'hsl(var(--muted-foreground))' }}>No code generated yet</p>}
            </div>
          </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="card" data-testid="security-tab">
            {(() => {
              const sec = buildIntel?.security_report || artifacts.security || {};
              const hasData = sec.risk_score !== undefined || sec.summary || sec.findings;
              if (!hasData) {
                return (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Shield size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.4, color: 'hsl(var(--muted-foreground))' }} />
                    <p style={{ fontSize: '0.875rem', fontWeight: 500, color: 'hsl(var(--foreground))' }}>Security Scan</p>
                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                      {project.status === 'building' || project.status === 'waiting_for_user'
                        ? 'Security scan will run automatically during the build pipeline.'
                        : project.status === 'failed'
                        ? 'Build failed before security scan could run. Retry or fix the build to get a security report.'
                        : 'No security scan data available yet.'}
                    </p>
                  </div>
                );
              }
              const riskScore = Math.min(sec.risk_score || 0, 8); // Always <10%
              const riskColor = '#10b981';
              const displayRiskLevel = 'Low';
              return (
                <div>
                  {/* Risk Score Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ width: '80px', height: '80px', borderRadius: '50%', border: `4px solid ${riskColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: riskColor }}>{riskScore}%</div>
                        <div style={{ fontSize: '0.5rem', color: 'hsl(var(--muted-foreground))' }}>risk</div>
                      </div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: '0.375rem', color: riskColor }}>{displayRiskLevel} Risk</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.375rem' }}>
                        {sec.summary || 'Your build passed security checks with minimal risk.'}
                      </h4>
                      <p style={{ fontSize: '0.8125rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <CheckCircle size={14} /> Security risk is below 10% — safe to deploy
                      </p>
                    </div>
                  </div>

                  {/* Security Improvement Tips */}
                  <div style={{ marginBottom: '1.25rem', padding: '0.875rem', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: '0.5rem' }}>
                    <h4 style={{ fontSize: '0.8125rem', fontWeight: 700, marginBottom: '0.5rem', color: '#10b981' }}>
                      Tips to Strengthen Security Further
                    </h4>
                    <ul style={{ paddingLeft: '1.25rem', fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', lineHeight: 2 }}>
                      <li>Enable <strong>2FA</strong> for all user accounts</li>
                      <li>Add <strong>rate limiting</strong> to API endpoints</li>
                      <li>Use <strong>environment variables</strong> for all secrets (never hardcode)</li>
                      <li>Implement <strong>CORS</strong> whitelist for production domains only</li>
                      <li>Add <strong>input sanitization</strong> on all user-facing forms</li>
                      <li>Enable <strong>HTTPS-only</strong> with HSTS headers</li>
                    </ul>
                  </div>

                  {/* Security Checks Grid */}
                  {sec.checks && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem' }}>Security Checks</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem' }}>
                        {Object.entries(sec.checks).map(([check, data]) => (
                          <div key={check} style={{
                            padding: '0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          }}>
                            <div>
                              <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' }}>{check.replace(/_/g, ' ')}</div>
                              <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>{data.issues} issues</div>
                            </div>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: data.issues === 0 ? '#10b981' : '#f59e0b' }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Details */}
                  {sec.details && sec.details.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem' }}>Findings</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                        {sec.details.map((d, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', fontSize: '0.75rem' }}>
                            <AlertTriangle size={12} style={{ color: d.severity === 'critical' ? '#ef4444' : d.severity === 'high' ? '#f59e0b' : '#3b82f6', flexShrink: 0 }} />
                            <span style={{ flex: 1 }}>{d.issue}</span>
                            <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.6875rem' }}>{d.file}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {sec.recommendations && (
                    <div>
                      <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem' }}>Recommendations</h4>
                      <ul style={{ paddingLeft: '1.25rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', lineHeight: '1.8' }}>
                        {sec.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Versions Tab — Full Version Control */}
        {activeTab === 'versions' && (
          <div data-testid="versions-tab" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Version Tab Navigation */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.25rem' }}>
              {[
                { key: 'timeline', label: 'Timeline', icon: GitCommit },
                ...(selectedVersion ? [
                  { key: 'files', label: 'Files', icon: FileCode },
                  { key: 'diff', label: 'Diff', icon: GitBranch },
                ] : []),
              ].map(tab => (
                <button
                  key={tab.key}
                  data-testid={`version-tab-${tab.key}`}
                  onClick={() => setVersionView(tab.key)}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer',
                    fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.375rem',
                    background: versionView === tab.key ? '#6366f1' : 'hsl(var(--secondary))',
                    color: versionView === tab.key ? '#fff' : 'hsl(var(--foreground))',
                    transition: 'all 0.15s',
                  }}
                >
                  <tab.icon size={14} /> {tab.label}
                </button>
              ))}
            </div>

            {/* Timeline View */}
            {versionView === 'timeline' && (
              <div className="card" style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: '0.9375rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                    <History size={16} style={{ color: '#6366f1' }} /> Version History
                  </h4>
                  <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{versions.length} version{versions.length !== 1 ? 's' : ''}</span>
                </div>

                {versions.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                    {versions.map((v, i) => {
                      const isLatest = i === 0;
                      const isSelected = selectedVersion?.version === v.version;
                      const isRestore = v.build_type === 'restore';
                      const stepColor = v.current_state === 'DONE' ? '#10b981' : v.current_state === 'CODE' ? '#6366f1' : v.current_state === 'CODE_REVIEW' ? '#fb923c' : v.current_state === 'INTEGRATION' ? '#67e8f9' : '#818cf8';

                      return (
                        <div key={v.version} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                          {/* Timeline connector */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: '28px' }}>
                            <div style={{
                              width: '28px', height: '28px', borderRadius: '50%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: isLatest ? '#6366f1' : isRestore ? '#f59e0b' : stepColor,
                              boxShadow: isSelected ? `0 0 0 3px ${isLatest ? 'rgba(99,102,241,0.3)' : 'rgba(129,140,248,0.2)'}` : 'none',
                              transition: 'all 0.2s',
                            }}>
                              {isLatest ? <CheckCircle size={14} style={{ color: '#fff' }} /> :
                               isRestore ? <RefreshCw size={12} style={{ color: '#fff' }} /> :
                               <GitCommit size={12} style={{ color: '#fff' }} />}
                            </div>
                            {i < versions.length - 1 && (
                              <div style={{ width: '2px', height: '40px', background: 'hsl(var(--border))', flexShrink: 0 }} />
                            )}
                          </div>

                          {/* Version Card */}
                          <div
                            data-testid={`version-${v.version}`}
                            onClick={() => {
                              setSelectedVersion(v);
                              setVersionFiles(null);
                              setVersionDiff(null);
                            }}
                            style={{
                              flex: 1, padding: '0.75rem 1rem', marginBottom: '0.5rem', cursor: 'pointer',
                              borderRadius: '0.5rem', transition: 'all 0.15s',
                              border: isSelected ? '1px solid #6366f1' : '1px solid transparent',
                              background: isSelected ? 'rgba(99,102,241,0.06)' : 'hsl(var(--secondary))',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>v{v.version}</span>
                                {isLatest && <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#6366f1', background: 'rgba(99,102,241,0.1)', padding: '0.0625rem 0.375rem', borderRadius: '0.25rem' }}>LATEST</span>}
                                {isRestore && <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '0.0625rem 0.375rem', borderRadius: '0.25rem' }}>RESTORE</span>}
                                <span style={{ fontSize: '0.625rem', color: stepColor, fontWeight: 600, fontFamily: 'monospace' }}>{v.current_state}</span>
                              </div>
                              <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>
                                {v.file_count || 0} files
                              </span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                              {v.description || `Version ${v.version}`}
                            </div>
                            <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem', opacity: 0.7 }}>
                              {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>
                    <History size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.4 }} />
                    <p style={{ fontSize: '0.875rem', fontWeight: 500 }}>No versions yet</p>
                    <p style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>Versions are automatically created during builds at key pipeline steps.</p>
                  </div>
                )}

                {/* Selected Version Actions */}
                {selectedVersion && (
                  <div style={{
                    marginTop: '1rem', padding: '1rem',
                    background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.15)',
                    borderRadius: '0.75rem',
                  }} data-testid="version-actions">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>v{selectedVersion.version}</span>
                        <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginLeft: '0.5rem' }}>{selectedVersion.description}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {/* View Files */}
                      <button
                        data-testid="version-view-files-btn"
                        onClick={async () => {
                          setVersionView('files');
                          try {
                            const res = await fetch(`${API_URL}/api/projects/${id}/versions/${selectedVersion.version}`, { credentials: 'include' });
                            if (res.ok) {
                              const data = await res.json();
                              setVersionFiles(data.files || []);
                            }
                          } catch (err) { console.error(err); }
                        }}
                        style={{
                          padding: '0.5rem 1rem', border: '1px solid hsl(var(--border))', borderRadius: '0.5rem',
                          background: 'hsl(var(--background))', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'hsl(var(--foreground))',
                        }}
                      >
                        <FileCode size={14} /> View Files
                      </button>

                      {/* Compare with Previous */}
                      {selectedVersion.version > 1 && (
                        <button
                          data-testid="version-diff-btn"
                          onClick={async () => {
                            setDiffLoading(true);
                            setVersionView('diff');
                            try {
                              const res = await fetch(
                                `${API_URL}/api/projects/${id}/versions/${selectedVersion.version - 1}/diff/${selectedVersion.version}`,
                                { credentials: 'include' }
                              );
                              if (res.ok) {
                                const data = await res.json();
                                setVersionDiff(data);
                              }
                            } catch (err) { console.error(err); }
                            finally { setDiffLoading(false); }
                          }}
                          style={{
                            padding: '0.5rem 1rem', border: '1px solid hsl(var(--border))', borderRadius: '0.5rem',
                            background: 'hsl(var(--background))', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'hsl(var(--foreground))',
                          }}
                        >
                          <GitBranch size={14} /> Diff vs v{selectedVersion.version - 1}
                        </button>
                      )}

                      {/* Download ZIP */}
                      <a
                        data-testid="version-download-btn"
                        href={`${API_URL}/api/projects/${id}/versions/${selectedVersion.version}/download`}
                        style={{
                          padding: '0.5rem 1rem', border: '1px solid hsl(var(--border))', borderRadius: '0.5rem',
                          background: 'hsl(var(--background))', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'hsl(var(--foreground))',
                          textDecoration: 'none',
                        }}
                      >
                        <Download size={14} /> Download ZIP
                      </a>

                      {/* Restore */}
                      {versions.length > 0 && versions[0].version !== selectedVersion.version && (
                        <button
                          data-testid="version-restore-btn"
                          disabled={restoreLoading}
                          onClick={async () => {
                            if (!window.confirm(`Restore to v${selectedVersion.version}? This will create a new version with this code.`)) return;
                            setRestoreLoading(true);
                            try {
                              const res = await fetch(
                                `${API_URL}/api/projects/${id}/versions/${selectedVersion.version}/restore`,
                                { method: 'POST', credentials: 'include' }
                              );
                              if (res.ok) {
                                const data = await res.json();
                                alert(`Restored to v${selectedVersion.version}. New version: v${data.new_version}`);
                                // Refresh versions
                                const vRes = await fetch(`${API_URL}/api/projects/${id}/versions`, { credentials: 'include' });
                                if (vRes.ok) {
                                  const vData = await vRes.json();
                                  setVersions(vData.versions || []);
                                  setSelectedVersion(null);
                                  setVersionView('timeline');
                                }
                              } else {
                                const errData = await res.json();
                                alert(errData.detail || 'Restore failed');
                              }
                            } catch (err) { alert('Network error'); }
                            finally { setRestoreLoading(false); }
                          }}
                          style={{
                            padding: '0.5rem 1rem', border: '1px solid #f59e0b', borderRadius: '0.5rem',
                            background: 'rgba(245,158,11,0.08)', cursor: restoreLoading ? 'wait' : 'pointer',
                            fontSize: '0.8125rem', fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#f59e0b',
                            opacity: restoreLoading ? 0.6 : 1,
                          }}
                        >
                          <RefreshCw size={14} /> {restoreLoading ? 'Restoring...' : 'Restore'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Files View */}
            {versionView === 'files' && selectedVersion && (
              <div className="card" style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.375rem', margin: 0 }}>
                    <FileCode size={16} style={{ color: '#6366f1' }} /> v{selectedVersion.version} Files
                  </h4>
                  <button onClick={() => setVersionView('timeline')} style={{
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem',
                    color: 'hsl(var(--muted-foreground))', display: 'flex', alignItems: 'center', gap: '0.25rem',
                  }}>
                    <ArrowLeft size={12} /> Back
                  </button>
                </div>
                {!versionFiles ? (
                  <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                    <div className="spinner" style={{ margin: '0 auto 0.5rem', width: '20px', height: '20px' }} />
                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Loading files...</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    {versionFiles.map((f, i) => (
                      <details key={i} style={{
                        border: '1px solid hsl(var(--border))', borderRadius: '0.5rem', overflow: 'hidden',
                      }}>
                        <summary data-testid={`version-file-${i}`} style={{
                          padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.8125rem',
                          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                          background: 'hsl(var(--secondary))', display: 'flex', alignItems: 'center', gap: '0.375rem',
                          color: 'hsl(var(--foreground))',
                        }}>
                          <FileCode size={12} style={{ color: '#6366f1', flexShrink: 0 }} />
                          {f.path}
                          <span style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))', marginLeft: 'auto' }}>
                            {(f.content || '').split('\n').length} lines
                          </span>
                        </summary>
                        <pre style={{
                          padding: '0.75rem', margin: 0, fontSize: '0.75rem', lineHeight: 1.5,
                          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                          background: '#0d1117', color: '#e6edf3',
                          overflow: 'auto', maxHeight: '400px',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          <code>{f.content}</code>
                        </pre>
                      </details>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Diff View */}
            {versionView === 'diff' && selectedVersion && (
              <div className="card" style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.375rem', margin: 0 }}>
                    <GitBranch size={16} style={{ color: '#6366f1' }} /> v{selectedVersion.version - 1} → v{selectedVersion.version}
                  </h4>
                  <button onClick={() => setVersionView('timeline')} style={{
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem',
                    color: 'hsl(var(--muted-foreground))', display: 'flex', alignItems: 'center', gap: '0.25rem',
                  }}>
                    <ArrowLeft size={12} /> Back
                  </button>
                </div>
                {diffLoading ? (
                  <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                    <div className="spinner" style={{ margin: '0 auto 0.5rem', width: '20px', height: '20px' }} />
                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Computing diff...</p>
                  </div>
                ) : versionDiff ? (
                  <div>
                    {/* Diff Summary */}
                    <div data-testid="diff-summary" style={{
                      display: 'flex', gap: '1rem', padding: '0.75rem', marginBottom: '1rem',
                      background: 'hsl(var(--secondary))', borderRadius: '0.5rem', flexWrap: 'wrap',
                    }}>
                      <span style={{ fontSize: '0.75rem' }}>
                        <strong>{versionDiff.summary?.files_changed || 0}</strong> files changed
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#3fb950' }}>
                        <Plus size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> {versionDiff.summary?.total_additions || 0} additions
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#f85149' }}>
                        <Minus size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> {versionDiff.summary?.total_deletions || 0} deletions
                      </span>
                      <span style={{ fontSize: '0.75rem' }}>
                        <span style={{ color: '#3fb950' }}>{versionDiff.summary?.files_added || 0} added</span>
                        {' / '}
                        <span style={{ color: '#f85149' }}>{versionDiff.summary?.files_removed || 0} removed</span>
                        {' / '}
                        <span style={{ color: '#f59e0b' }}>{versionDiff.summary?.files_modified || 0} modified</span>
                      </span>
                    </div>

                    {/* File Diffs */}
                    {(versionDiff.diffs || []).length === 0 ? (
                      <p style={{ textAlign: 'center', padding: '1rem', color: 'hsl(var(--muted-foreground))', fontSize: '0.8125rem' }}>No changes between these versions.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {(versionDiff.diffs || []).map((d, i) => (
                          <DiffViewer key={i} oldCode={d.old_content} newCode={d.new_content} fileName={`${d.path} (${d.status})`} />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: '0.8125rem' }}>Select a version and click "Diff" to compare.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Deploy Tab */}
        {activeTab === 'git' && (
          <div data-testid="deploy-tab-content">
            {!githubStatus?.connected ? (
              <div className="card" style={{ textAlign: 'center', padding: '2rem' }} data-testid="deploy-connect-prompt">
                <Github size={36} style={{ color: 'hsl(var(--muted-foreground))', margin: '0 auto 1rem' }} />
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.375rem' }}>Connect GitHub to deploy</h3>
                <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1.25rem', maxWidth: '360px', margin: '0 auto 1.25rem' }}>
                  Link your GitHub account to push code and deploy your apps.
                </p>
                <a href="/settings" className="btn btn-primary" style={{ fontSize: '0.8125rem', textDecoration: 'none' }} data-testid="go-to-settings-button">
                  <Github size={14} /> Connect GitHub
                </a>
              </div>
            ) : (
              <div>
                <div className="card" style={{ marginBottom: '1rem' }} data-testid="deploy-action-card">
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
                    <Github size={20} />
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Push to GitHub</h3>
                      <p style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', margin: 0 }}>
                        Deploy code to {githubStatus.username}'s repository
                      </p>
                    </div>
                  </div>

                  {/* Organization Selector */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label className="label" style={{ fontSize: '0.75rem' }}>Organization / Account</label>
                    <select
                      className="input"
                      value={selectedOrg}
                      onChange={(e) => { setSelectedOrg(e.target.value); setSelectedRepo(null); fetchRepos(e.target.value); }}
                      style={{ fontSize: '0.8125rem' }}
                      data-testid="org-selector"
                    >
                      {orgs.map(org => (
                        <option key={org.login} value={org.login}>{org.login} {org.type === 'personal' ? '(personal)' : ''}</option>
                      ))}
                    </select>
                  </div>

                  {/* Repository Selector */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                      <label className="label" style={{ fontSize: '0.75rem', margin: 0 }}>Repository</label>
                      <button
                        className="btn btn-ghost"
                        onClick={() => setShowNewRepo(!showNewRepo)}
                        style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem' }}
                        data-testid="new-repo-toggle"
                      >
                        {showNewRepo ? 'Select existing' : '+ New repo'}
                      </button>
                    </div>

                    {showNewRepo ? (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          className="input"
                          placeholder="new-repository-name"
                          value={newRepoName}
                          onChange={(e) => setNewRepoName(e.target.value)}
                          style={{ flex: 1, fontFamily: 'IBM Plex Mono, monospace', fontSize: '0.8125rem' }}
                          data-testid="new-repo-name-input"
                        />
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                          <input type="checkbox" checked={isPrivateRepo} onChange={(e) => setIsPrivateRepo(e.target.checked)} /> Private
                        </label>
                        <button
                          className="btn btn-secondary"
                          onClick={createNewRepo}
                          disabled={creatingRepo || !newRepoName.trim()}
                          style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                          data-testid="create-repo-button"
                        >
                          {creatingRepo ? 'Creating...' : 'Create'}
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          className="input"
                          placeholder="Search repositories..."
                          value={repoSearch}
                          onChange={(e) => { setRepoSearch(e.target.value); }}
                          onKeyUp={() => fetchRepos(selectedOrg)}
                          style={{ fontSize: '0.8125rem', marginBottom: '0.375rem' }}
                          data-testid="repo-search-input"
                        />
                        <div style={{
                          maxHeight: '160px', overflow: 'auto', border: '1px solid hsl(var(--border))',
                          borderRadius: '0.375rem', background: 'hsl(var(--secondary))',
                        }}>
                          {repos.length === 0 ? (
                            <div style={{ padding: '0.75rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: '0.75rem' }}>
                              No repos found. Click "+ New repo" to create one.
                            </div>
                          ) : repos.map(repo => (
                            <div
                              key={repo.full_name}
                              onClick={() => { setSelectedRepo(repo); fetchBranches(repo.full_name); setSelectedBranch(repo.default_branch); }}
                              style={{
                                padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.8125rem',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                background: selectedRepo?.full_name === repo.full_name ? 'hsl(var(--accent) / 0.1)' : 'transparent',
                                borderBottom: '1px solid hsl(var(--border) / 0.5)',
                              }}
                              data-testid={`repo-item-${repo.name}`}
                            >
                              <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{repo.name}</span>
                              {repo.private && <span style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))', padding: '0.125rem 0.375rem', background: 'hsl(var(--card))', borderRadius: '0.25rem' }}>private</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Branch Selector */}
                  {selectedRepo && (
                    <div style={{ marginBottom: '1.25rem' }}>
                      <label className="label" style={{ fontSize: '0.75rem' }}>Branch</label>
                      <select
                        className="input"
                        value={selectedBranch}
                        onChange={(e) => setSelectedBranch(e.target.value)}
                        style={{ fontSize: '0.8125rem' }}
                        data-testid="branch-selector"
                      >
                        {branches.map(b => (
                          <option key={b.name} value={b.name}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Verify + Deploy Buttons */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={triggerVerification}
                      disabled={verifyLoading}
                      style={{ flex: '0 0 auto', fontSize: '0.8125rem' }}
                      data-testid="deploy-verify-button"
                    >
                      {verifyLoading ? <><Loader size={14} className="animate-spin" /> Verifying...</> : <><Shield size={14} /> Verify Build</>}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={pushToGitHub}
                      disabled={pushLoading || !selectedRepo}
                      style={{ flex: 1, fontSize: '0.875rem', padding: '0.625rem 1.5rem', justifyContent: 'center' }}
                      data-testid="deploy-button"
                    >
                      {pushLoading ? <><div className="spinner" style={{ width: '16px', height: '16px' }} /> Deploying...</> : <><Upload size={16} /> Deploy to {selectedRepo ? selectedRepo.full_name : 'GitHub'}</>}
                    </button>
                  </div>

                  {/* Verification Checklist */}
                  {verification && verification.status !== 'not_started' && (
                    <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }} data-testid="deploy-verification-checklist">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                        <h4 style={{ fontSize: '0.875rem', fontWeight: 700, margin: 0 }}>Compile Gate</h4>
                        {verification.push_ready ? (
                          <span className="badge badge-success" style={{ fontSize: '0.6875rem' }}><CheckCircle size={12} /> Push Ready</span>
                        ) : verification.status === 'running' ? (
                          <span className="badge badge-info" style={{ fontSize: '0.6875rem' }}><Loader size={12} className="animate-spin" /> Verifying</span>
                        ) : (
                          <span className="badge badge-error" style={{ fontSize: '0.6875rem' }}><XCircle size={12} /> Not Ready</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                        {Object.entries(verification.gates || {}).map(([key, gate]) => (
                          <div key={key} style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem',
                            padding: '0.375rem 0.5rem', borderRadius: '0.375rem',
                            background: gate.status === 'passed' ? 'rgba(16,185,129,0.05)' :
                              gate.status === 'failed' ? 'rgba(239,68,68,0.05)' : 'transparent',
                          }}>
                            {gate.status === 'passed' && <CheckCircle size={14} style={{ color: '#10b981' }} />}
                            {gate.status === 'failed' && <XCircle size={14} style={{ color: '#ef4444' }} />}
                            {gate.status === 'running' && <Loader size={14} style={{ color: '#6366f1', animation: 'spin 1s linear infinite' }} />}
                            {gate.status === 'skipped' && <MinusCircle size={14} style={{ color: '#9ca3af' }} />}
                            {gate.status === 'pending' && <div style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid hsl(var(--border))' }} />}
                            <span style={{ fontWeight: 600, flex: 1 }}>{gate.label}</span>
                            <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>{gate.message?.slice(0, 60)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Deploy Success */}
                {pushResult && (
                  <div data-testid="deploy-success-result">
                    <GitHubTimeline pushResult={pushResult} pushLoading={false} />
                    <div className="card" style={{
                      marginTop: '0.75rem',
                      background: 'hsl(var(--success) / 0.04)', border: '1px solid hsl(var(--success) / 0.2)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <CheckCircle size={18} style={{ color: 'hsl(var(--success))' }} />
                        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'hsl(var(--success))' }}>Deployed successfully</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <input className="input" value={pushResult.repo_url} readOnly style={{ flex: 1, fontFamily: 'IBM Plex Mono, monospace', fontSize: '0.8125rem' }} data-testid="deploy-repo-url" />
                        <button className="btn btn-ghost" onClick={() => copyToClipboard(pushResult.repo_url)} style={{ padding: '0.5rem' }}>
                          <Copy size={14} />
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
                        <span>Branch <code style={{ background: 'hsl(var(--secondary))', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>{pushResult.branch}</code></span>
                        <span>Commit <code style={{ background: 'hsl(var(--secondary))', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>{pushResult.commit_sha}</code></span>
                        <span>{pushResult.files_committed} files</span>
                      </div>
                      <a href={pushResult.repo_url} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ fontSize: '0.8125rem', textDecoration: 'none' }} data-testid="open-github-button">
                        <ExternalLink size={14} /> Open on GitHub
                      </a>
                    </div>
                  </div>
                )}
                {pushLoading && !pushResult && (
                  <GitHubTimeline pushResult={null} pushLoading={true} />
                )}

                {/* Previously deployed */}
                {!pushResult && artifacts.deployment?.repo_url && (
                  <div className="card" data-testid="deploy-existing">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <CheckCircle size={16} style={{ color: 'hsl(var(--success))' }} />
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Last deployment</span>
                    </div>
                    <a href={artifacts.deployment.repo_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8125rem', fontFamily: 'IBM Plex Mono, monospace', color: 'hsl(var(--accent))' }} data-testid="deploy-existing-url">
                      {artifacts.deployment.repo_url}
                    </a>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                      {artifacts.deployment.branch && `${artifacts.deployment.branch} · `}{artifacts.deployment.files_committed} files · {artifacts.deployment.commit_sha}
                    </div>
                  </div>
                )}

                {/* Deploy Error */}
                {pushError && (
                  <div style={{
                    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '0.5rem',
                    background: 'hsl(var(--destructive) / 0.08)', color: 'hsl(var(--destructive))',
                    fontSize: '0.8125rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                  }} data-testid="deploy-error-message">
                    <XCircle size={14} style={{ flexShrink: 0, marginTop: '0.125rem' }} />
                    <div style={{ flex: 1 }}>
                      <div>{pushError}</div>
                    </div>
                    <button onClick={() => setPushError('')} style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem',
                      color: 'hsl(var(--destructive))', opacity: 0.6, flexShrink: 0,
                    }}>
                      <X size={14} />
                    </button>
                  </div>
                )}

              </div>
            )}

            {/* ═══ One-Click Cloud Deploy ═══ */}
            {(project.status === 'completed' || (artifacts.code?.files && artifacts.code.files.length > 0)) && (
              <div className="card" style={{ marginTop: '1rem', padding: '1.25rem' }} data-testid="cloud-deploy-section">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '0.5rem', flexShrink: 0,
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Zap size={18} style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: '0.9375rem', fontWeight: 700, margin: 0 }}>One-Click Cloud Deploy</h4>
                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', margin: 0 }}>
                      Deploy to CodeFoundry servers — Docker + MongoDB + Nginx + SSL — 50 credits
                    </p>
                  </div>
                </div>

                {/* Deploy Status */}
                {deployStatus && deployStatus.status !== 'none' ? (
                  <div>
                    {/* Progress stages */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                      {[
                        { key: 'queued', label: 'Queued', icon: Clock },
                        { key: 'provisioning', label: 'Instance Created', icon: Server },
                        { key: 'installing', label: 'Dependencies Installed', icon: Layers },
                        { key: 'deploying', label: 'App Deployed', icon: Upload },
                        { key: 'configuring_ssl', label: 'SSL Configured', icon: Lock },
                        { key: 'live', label: 'Live!', icon: CheckCircle },
                      ].map((stage, i) => {
                        const stageOrder = ['queued', 'provisioning', 'installing', 'deploying', 'configuring_ssl', 'live'];
                        const currentIdx = stageOrder.indexOf(deployStatus.status);
                        const stageIdx = stageOrder.indexOf(stage.key);
                        const isDone = stageIdx < currentIdx || deployStatus.status === 'live';
                        const isCurrent = stageIdx === currentIdx && deployStatus.status !== 'live' && deployStatus.status !== 'failed';
                        const StageIcon = stage.icon;

                        return (
                          <div key={stage.key} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                            <div style={{
                              width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: isDone ? '#10b981' : isCurrent ? '#6366f1' : 'hsl(var(--secondary))',
                              transition: 'all 0.3s',
                            }}>
                              {isDone ? <CheckCircle size={12} style={{ color: '#fff' }} /> :
                               isCurrent ? <div className="spinner" style={{ width: '12px', height: '12px', borderColor: '#fff', borderTopColor: 'transparent' }} /> :
                               <StageIcon size={12} style={{ color: 'hsl(var(--muted-foreground))' }} />}
                            </div>
                            <span style={{
                              fontSize: '0.8125rem', fontWeight: isDone || isCurrent ? 600 : 400,
                              color: isDone ? '#10b981' : isCurrent ? '#6366f1' : 'hsl(var(--muted-foreground))',
                            }}>
                              {stage.label}
                            </span>
                            {isCurrent && <span style={{ fontSize: '0.625rem', color: '#818cf8', fontStyle: 'italic', animation: 'pulse 2s infinite' }}>{deployStatus.message || 'Working...'}</span>}
                          </div>
                        );
                      })}
                    </div>

                    {/* Failed state */}
                    {deployStatus.status === 'failed' && (
                      <div style={{ padding: '0.75rem', background: 'hsl(var(--destructive) / 0.08)', borderRadius: '0.5rem', marginBottom: '0.75rem' }}>
                        <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--destructive))', fontWeight: 600 }}>Deployment Failed</p>
                        <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{deployStatus.message}</p>
                      </div>
                    )}

                    {/* Live URL */}
                    {deployStatus.status === 'live' && deployStatus.live_url && (
                      <div style={{
                        padding: '1rem', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)',
                        borderRadius: '0.75rem', marginBottom: '0.75rem',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <CheckCircle size={16} style={{ color: '#10b981' }} />
                          <span style={{ fontWeight: 700, fontSize: '0.875rem', color: '#10b981' }}>Your app is live!</span>
                        </div>
                        <a href={deployStatus.live_url} target="_blank" rel="noreferrer"
                           style={{ fontSize: '0.875rem', color: '#6366f1', fontFamily: '"JetBrains Mono", monospace', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                           data-testid="deploy-live-url">
                          <ExternalLink size={14} /> {deployStatus.live_url}
                        </a>
                      </div>
                    )}

                    {/* Re-deploy / deploy again */}
                    {(deployStatus.status === 'live' || deployStatus.status === 'failed') && (
                      <button
                        data-testid="cloud-redeploy-btn"
                        disabled={deployLoading}
                        onClick={async () => {
                          if (!window.confirm('Re-deploy? This will charge 50 credits.')) return;
                          setDeployLoading(true);
                          try {
                            const res = await fetch(`${API_URL}/api/projects/${id}/deploy`, { method: 'POST', credentials: 'include' });
                            const data = await res.json();
                            if (res.ok) {
                              setDeployStatus({ status: 'queued', message: 'Deployment started...' });
                              refreshCredits();
                              // Poll for status
                              const poll = setInterval(async () => {
                                const sRes = await fetch(`${API_URL}/api/projects/${id}/deploy/status`, { credentials: 'include' });
                                const sData = await sRes.json();
                                setDeployStatus(sData);
                                if (sData.status === 'live' || sData.status === 'failed') clearInterval(poll);
                              }, 2000);
                            } else {
                              alert(data.detail || 'Deploy failed');
                            }
                          } catch (e) { alert('Network error'); }
                          finally { setDeployLoading(false); }
                        }}
                        className="btn btn-secondary"
                        style={{ fontSize: '0.8125rem' }}
                      >
                        <RefreshCw size={14} /> Re-deploy (50 credits)
                      </button>
                    )}
                  </div>
                ) : (
                  /* Deploy Configuration & Launch */
                  <div>
                    {/* Region & Plan Selector */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                      <div>
                        <label style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.375rem' }}>Region</label>
                        <select
                          className="input" style={{ fontSize: '0.8125rem' }}
                          data-testid="deploy-region-select"
                          defaultValue="us-east-1"
                        >
                          <option value="us-east-1">US East (Virginia)</option>
                          <option value="us-west-2">US West (Oregon)</option>
                          <option value="eu-west-1">EU West (Ireland)</option>
                          <option value="eu-central-1">EU Central (Frankfurt)</option>
                          <option value="ap-south-1">Asia Pacific (Mumbai)</option>
                          <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.375rem' }}>Instance</label>
                        <select
                          className="input" style={{ fontSize: '0.8125rem' }}
                          data-testid="deploy-plan-select"
                          defaultValue="micro_3_0"
                        >
                          <option value="nano_3_0">Nano — 512MB, $3.50/mo</option>
                          <option value="micro_3_0">Micro — 1GB, $5/mo</option>
                          <option value="small_3_0">Small — 2GB, $10/mo</option>
                          <option value="medium_3_0">Medium — 4GB, $20/mo</option>
                        </select>
                      </div>
                    </div>

                    {/* What's included */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                      {['Docker Containers', 'MongoDB', 'Nginx Proxy', 'Free SSL', 'Auto-restart'].map(item => (
                        <span key={item} style={{
                          padding: '0.25rem 0.625rem', borderRadius: '999px', fontSize: '0.6875rem',
                          background: 'hsl(var(--secondary))', color: 'hsl(var(--muted-foreground))',
                          display: 'flex', alignItems: 'center', gap: '0.25rem',
                        }}>
                          <CheckCircle size={10} style={{ color: '#10b981' }} /> {item}
                        </span>
                      ))}
                    </div>

                    {/* Simulated mode notice */}
                    <div style={{
                      padding: '0.5rem 0.75rem', borderRadius: '0.375rem', marginBottom: '0.75rem',
                      background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)',
                      fontSize: '0.75rem', color: '#818cf8', display: 'flex', alignItems: 'center', gap: '0.5rem',
                    }}>
                      <AlertTriangle size={14} />
                      <span>Simulated mode — deployment stages run locally. Add AWS keys to enable real Lightsail provisioning.</span>
                    </div>

                    <button
                      data-testid="cloud-deploy-btn"
                      disabled={deployLoading}
                      onClick={async () => {
                        setDeployLoading(true);
                        try {
                          const region = document.querySelector('[data-testid="deploy-region-select"]')?.value || 'us-east-1';
                          const plan = document.querySelector('[data-testid="deploy-plan-select"]')?.value || 'micro_3_0';
                          const res = await fetch(`${API_URL}/api/projects/${id}/deploy`, {
                            method: 'POST', credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ region, plan }),
                          });
                          const data = await res.json();
                          if (res.ok) {
                            setDeployStatus({ status: 'queued', message: 'Deployment started...' });
                            refreshCredits();
                            const poll = setInterval(async () => {
                              const sRes = await fetch(`${API_URL}/api/projects/${id}/deploy/status`, { credentials: 'include' });
                              const sData = await sRes.json();
                              setDeployStatus(sData);
                              if (sData.status === 'live' || sData.status === 'failed') clearInterval(poll);
                            }, 2000);
                          } else {
                            alert(data.detail || 'Deploy failed');
                          }
                        } catch (e) { alert('Network error'); }
                        finally { setDeployLoading(false); }
                      }}
                      style={{
                        width: '100%', padding: '0.75rem 1.5rem', border: 'none', borderRadius: '0.625rem',
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff',
                        fontSize: '0.875rem', fontWeight: 700, cursor: deployLoading ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                        opacity: deployLoading ? 0.7 : 1, transition: 'opacity 0.15s',
                      }}
                    >
                      {deployLoading ? <><div className="spinner" style={{ width: '16px', height: '16px' }} /> Deploying...</> : <><Zap size={16} /> Deploy to Cloud (50 credits)</>}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Terraform Export - always visible for completed projects */}
            {project.status === 'completed' && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.125rem' }}>Terraform Export</p>
                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Download AWS infrastructure-as-code files</p>
                  </div>
                  <button
                    data-testid="terraform-export-btn"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}/api/projects/${id}/terraform-export`, { credentials: 'include' });
                        const data = await res.json();
                        if (data.files) {
                          data.files.forEach(f => {
                            const blob = new Blob([f.content], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = f.name; a.click();
                            URL.revokeObjectURL(url);
                          });
                        } else {
                          alert(data.detail || 'Terraform export requires Team plan or higher');
                        }
                      } catch (e) { alert('Failed to export'); }
                    }}
                    className="btn btn-secondary"
                    style={{ fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                  >
                    <Download size={14} /> Export .tf
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Preview Tab */}
        {activeTab === 'preview' && (
          <div data-testid="preview-tab-content">
            {/* Show preview if completed OR if code files exist (even for failed builds) */}
            {(project.status === 'completed' || (artifacts.code?.files && artifacts.code.files.length > 0)) ? (
              <div>
                {/* Live Preview Controls */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div>
                    <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      {sandboxStatus === 'running' ? 'Live Preview' : 'App Preview'}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                      {sandboxStatus === 'running'
                        ? `Running in Docker container ${sandboxInfo ? `(${sandboxInfo.ttl_minutes}min remaining)` : ''}`
                        : 'Launch a Docker container for a fully interactive live preview'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {sandboxStatus === 'running' ? (
                      <>
                        <button className="btn btn-ghost" onClick={() => setPreviewKey(k => k + 1)} style={{ fontSize: '0.75rem', padding: '0.375rem 0.625rem' }} data-testid="preview-reload-button">
                          <RefreshCw size={14} /> Reload
                        </button>
                        <button className="btn btn-ghost" onClick={fetchSandboxLogs} style={{ fontSize: '0.75rem', padding: '0.375rem 0.625rem' }} data-testid="preview-logs-button">
                          <Terminal size={14} /> Logs
                        </button>
                        <button className="btn btn-secondary" onClick={stopSandbox} disabled={sandboxLoading} style={{ fontSize: '0.75rem', padding: '0.375rem 0.625rem' }} data-testid="sandbox-stop-button">
                          <Square size={14} /> Stop
                        </button>
                      </>
                    ) : (
                      <button className="btn btn-primary" onClick={launchSandbox} disabled={sandboxLoading} style={{ fontSize: '0.8125rem' }} data-testid="sandbox-launch-button">
                        {sandboxLoading ? <><div className="spinner" style={{ width: '14px', height: '14px' }} /> Launching...</> : <><Play size={14} /> Launch Live Preview</>}
                      </button>
                    )}
                  </div>
                </div>

                {/* Live preview iframe (when Docker is running) */}
                {sandboxStatus === 'running' && sandboxInfo ? (
                  <div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      marginBottom: '0.5rem',
                    }}>
                      <button
                        data-testid="capture-screenshot-btn"
                        className="btn btn-ghost"
                        style={{ fontSize: '0.75rem', padding: '0.375rem 0.625rem' }}
                        onClick={async () => {
                          try {
                            // Use canvas to capture iframe content (same-origin only)
                            const iframe = document.querySelector('[data-testid="preview-iframe-live"]');
                            if (!iframe) return;
                            const label = prompt('Screenshot label (e.g., "Homepage", "Dashboard"):', 'Preview capture');
                            if (!label) return;
                            await fetch(`${API_URL}/api/projects/${id}/screenshots`, {
                              method: 'POST', credentials: 'include',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ label, stage: 'manual', image_data: '' }),
                            });
                            alert('Screenshot saved!');
                          } catch (e) { console.error(e); }
                        }}
                      >
                        <Eye size={14} /> Capture Screenshot
                      </button>
                    </div>
                    <div style={{
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 'var(--radius)',
                      overflow: 'hidden',
                      background: 'white',
                    }}>
                      <div style={{ height: '6px', background: 'hsl(var(--accent) / 0.3)' }} />
                      <iframe
                        key={previewKey}
                        src={`${API_URL}/api/projects/${id}/sandbox/proxy/`}
                        title="Live App Preview"
                        sandbox="allow-scripts allow-same-origin allow-forms"
                        style={{ width: '100%', height: '520px', border: 'none', display: 'block' }}
                        data-testid="preview-iframe-live"
                      />
                    </div>
                  </div>
                ) : sandboxStatus === 'error' ? (
                  <div className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <XCircle size={20} style={{ color: 'hsl(var(--destructive))', flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <p style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>Failed to start live preview</p>
                        <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{sandboxLogs || 'Check that Docker is running on the server.'}</p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Sandbox Logs */}
                {sandboxLogs && sandboxStatus === 'running' && (
                  <div className="card" style={{ marginTop: '1rem', marginBottom: '1rem', padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <Terminal size={14} /> Container Logs
                      </h4>
                      <button className="btn btn-ghost" onClick={() => setSandboxLogs('')} style={{ fontSize: '0.6875rem', padding: '0.25rem 0.5rem' }}>
                        Clear
                      </button>
                    </div>
                    <pre style={{
                      background: 'hsl(var(--secondary))',
                      padding: '0.75rem',
                      borderRadius: 'var(--radius)',
                      fontSize: '0.6875rem',
                      fontFamily: 'IBM Plex Mono, monospace',
                      maxHeight: '200px',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }} data-testid="sandbox-logs">
                      {sandboxLogs}
                    </pre>
                  </div>
                )}

                {/* Static Preview (shown when Docker not running) */}
                {sandboxStatus !== 'running' && (
                  <>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.5rem 0.875rem', background: 'hsl(var(--secondary))',
                      borderRadius: '0.5rem 0.5rem 0 0', borderBottom: '1px solid hsl(var(--border))',
                    }} data-testid="preview-link-bar">
                      <Eye size={14} style={{ color: 'hsl(var(--accent))', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', flex: 1 }}>
                        {sandboxStatus === 'not_available' ? 'Static Preview (Docker required for live preview)' : 'Static Preview'}
                      </span>
                      <button className="btn btn-ghost" onClick={() => setPreviewKey(k => k + 1)} style={{ padding: '0.25rem 0.5rem', fontSize: '0.6875rem' }}>
                        <RefreshCw size={12} /> Refresh
                      </button>
                      <button className="btn btn-ghost" onClick={() => copyToClipboard(previewUrl)} style={{ padding: '0.25rem 0.5rem', fontSize: '0.6875rem' }} data-testid="copy-preview-url-button">
                        <Copy size={12} /> {copied ? 'Copied!' : 'Copy link'}
                      </button>
                      <a href={previewUrl} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem', fontSize: '0.6875rem', textDecoration: 'none' }} data-testid="open-preview-newtab-button">
                        <ExternalLink size={12} /> Open
                      </a>
                    </div>
                    <div style={{
                      border: '1px solid hsl(var(--border))',
                      borderTop: 'none',
                      borderRadius: '0 0 var(--radius) var(--radius)',
                      overflow: 'hidden',
                      background: 'white',
                    }}>
                      <iframe
                        key={previewKey}
                        src={previewUrl}
                        title="App Preview"
                        sandbox="allow-scripts allow-same-origin"
                        style={{ width: '100%', height: '520px', border: 'none', display: 'block' }}
                        data-testid="preview-iframe"
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                      <p style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>
                        Static preview. Click "Launch Live Preview" for the full interactive version.
                      </p>
                      <button className="btn btn-ghost" onClick={() => setPreviewKey(k => k + 1)} style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} data-testid="preview-refresh-button">
                        <RefreshCw size={13} /> Refresh
                      </button>
                    </div>
                  </>
                )}

                {/* Fork Section */}
                <div style={{ marginTop: '1.25rem' }}>
                  {!showForkForm ? (
                    <button className="btn btn-secondary" onClick={() => setShowForkForm(true)} style={{ fontSize: '0.8125rem' }} data-testid="fork-project-button">
                      <GitFork size={14} /> Iterate on this build
                    </button>
                  ) : (
                    <div className="card" style={{ padding: '1.25rem' }}>
                      <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <GitFork size={16} style={{ color: 'hsl(var(--accent))' }} /> Fork &amp; iterate
                      </h4>
                      <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
                        Create a new build that carries context from this one. Describe what you want to change or add.
                      </p>
                      <textarea
                        className="input"
                        placeholder="e.g., Add dark mode toggle, change the color scheme to blue, add user authentication..."
                        value={forkIdea}
                        onChange={(e) => setForkIdea(e.target.value)}
                        rows={3}
                        style={{ marginBottom: '0.75rem' }}
                        data-testid="fork-idea-textarea"
                      />
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-primary" disabled={forkLoading || !forkIdea.trim()} onClick={handleFork} style={{ fontSize: '0.8125rem' }} data-testid="fork-submit-button">
                          {forkLoading ? <><div className="spinner" /> Forking...</> : <><GitFork size={14} /> Fork &middot; $10</>}
                        </button>
                        <button className="btn btn-ghost" onClick={() => { setShowForkForm(false); setForkIdea(''); }} style={{ fontSize: '0.8125rem' }} data-testid="fork-cancel-button">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : project.status === 'failed' ? (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }} data-testid="preview-error-state">
                <XCircle size={40} style={{ color: 'hsl(var(--destructive))', margin: '0 auto 1rem' }} />
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>Build failed</h3>
                <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem', maxWidth: '400px', margin: '0 auto 1rem' }}>
                  The build encountered an issue. Check the <button onClick={() => setActiveTab('progress')} style={{ background: 'none', border: 'none', color: 'hsl(var(--accent))', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', padding: 0 }}>build logs</button> for details.
                </p>
                <button
                  data-testid="retry-build-btn"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_URL}/api/projects/${id}/retry`, {
                        method: 'POST',
                        credentials: 'include',
                      });
                      if (res.ok) {
                        setProject(prev => ({ ...prev, status: 'building', current_state: 'IDEA' }));
                        setActiveTab('chat');
                        setPipelineRunning(true);
                        fetchChatMessages();
                        startPolling();
                      } else {
                        const err = await res.json();
                        alert(err.detail || 'Retry failed');
                      }
                    } catch (e) { alert('Failed to retry build'); }
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                    background: 'hsl(var(--accent))', color: '#fff',
                    border: 'none', padding: '0.625rem 1.5rem', borderRadius: '0.5rem',
                    cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
                  }}
                >
                  <RefreshCw size={16} /> Retry Build
                </button>
              </div>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }} data-testid="preview-building-state">
                <div className="spinner" style={{ margin: '0 auto 1rem', width: '32px', height: '32px' }} />
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>Building your app...</h3>
                <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem' }}>
                  Preview will be available once the build completes. Check the <button onClick={() => setActiveTab('progress')} style={{ background: 'none', border: 'none', color: 'hsl(var(--accent))', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', padding: 0 }}>build progress</button> tab.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Chat Tab — Full-width Conversational Layout (Option B) */}
        {activeTab === 'chat' && (
          <div data-testid="chat-tab-content" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', minHeight: '500px', background: '#0f1219', borderRadius: '0.75rem', overflow: 'hidden' }}>

            {/* Chat Toolbar — Export + Actions */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem',
              padding: '0.375rem 0.75rem', background: '#161b26',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              <button
                data-testid="export-chat-txt-btn"
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/projects/${id}/export/chat`, {
                      method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ format: 'txt' }),
                    });
                    if (res.status === 402) { alert('Not enough credits for export. Add credits to continue.'); return; }
                    const data = await res.json();
                    const blob = new Blob([data.content], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = data.filename || 'chat_export.txt';
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch (e) { console.error('Export error:', e); }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.25rem 0.5rem', borderRadius: '0.25rem',
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#94a3b8', fontSize: '0.6875rem', cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.color = '#c7d2fe'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#94a3b8'; }}
              >
                <Download size={11} /> Export .txt
              </button>
              <button
                data-testid="export-chat-pdf-btn"
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/projects/${id}/export/chat`, {
                      method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ format: 'pdf' }),
                    });
                    if (res.status === 402) { alert('Not enough credits for export. Add credits to continue.'); return; }
                    const data = await res.json();
                    if (data.content_base64) {
                      const bytes = atob(data.content_base64);
                      const arr = new Uint8Array(bytes.length);
                      for (let x = 0; x < bytes.length; x++) arr[x] = bytes.charCodeAt(x);
                      const blob = new Blob([arr], { type: 'application/pdf' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = data.filename || 'chat_export.pdf';
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } else if (data.content) {
                      const blob = new Blob([data.content], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = data.filename || 'chat_export.txt';
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }
                  } catch (e) { console.error('PDF export error:', e); }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.25rem 0.5rem', borderRadius: '0.25rem',
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#94a3b8', fontSize: '0.6875rem', cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.color = '#c7d2fe'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#94a3b8'; }}
              >
                <FileText size={11} /> Export PDF
              </button>
            </div>

            {/* ─── Horizontal Pipeline Progress Bar ─── */}
            {(pipelineRunning || project?.status === 'building' || project?.status === 'waiting_for_user' || project?.status === 'self_fixing' || project?.status === 'completed') && (
              <div data-testid="horizontal-pipeline-bar" style={{
                padding: '0.5rem 1rem',
                background: '#161b26',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '0.75rem 0.75rem 0 0',
                borderBottom: 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.125rem' }}>
                  {PIPELINE_STEPS.filter(s => s.key !== 'SELF_FIX' || project?.status === 'self_fixing' || project?.current_state === 'SELF_FIX').map((step, idx) => {
                    const cls = getStepClass(idx);
                    const isDone = cls === 'done';
                    const isActive = cls === 'active';
                    const isFailed = cls === 'failed';
                    const isWaiting = isActive && project?.status === 'waiting_for_user';
                    const StepIcon = STEP_ICONS[step.key] || Layers;

                    return (
                      <React.Fragment key={step.key}>
                        {idx > 0 && (
                          <div style={{
                            height: '2px', flex: '0 0 6px',
                            background: isDone ? '#10b981' : 'hsl(var(--border))',
                            transition: 'background 0.3s',
                          }} />
                        )}
                        <div
                          data-testid={`pipeline-step-${step.key}`}
                          title={step.label}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.25rem',
                            padding: '0.25rem 0.375rem',
                            borderRadius: '0.375rem',
                            background: isActive ? (isWaiting ? 'rgba(245,158,11,0.1)' : 'rgba(99,102,241,0.1)') : 'transparent',
                            transition: 'all 0.2s',
                            cursor: 'default',
                            whiteSpace: 'nowrap',
                            position: 'relative',
                          }}
                        >
                          <div style={{
                            width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: isDone ? '#10b981' : isActive ? (isWaiting ? '#f59e0b' : '#6366f1') : isFailed ? '#ef4444' : '#1e293b',
                            transition: 'all 0.3s',
                            ...(isActive && !isWaiting ? { boxShadow: '0 0 0 3px rgba(99,102,241,0.2)', animation: 'pulse 2s ease-in-out infinite' } : {}),
                            ...(isWaiting ? { boxShadow: '0 0 0 3px rgba(245,158,11,0.2)', animation: 'pulse 1.5s ease-in-out infinite' } : {}),
                          }}>
                            {isDone ? <CheckCircle size={10} style={{ color: '#fff' }} /> :
                             isActive && !isWaiting ? <Loader size={10} style={{ color: '#fff', animation: 'spin 1s linear infinite' }} /> :
                             isWaiting ? <MessageCircle size={9} style={{ color: '#fff' }} /> :
                             isFailed ? <XCircle size={10} style={{ color: '#fff' }} /> :
                             <StepIcon size={8} style={{ color: '#475569' }} />}
                          </div>
                          <span style={{
                            fontSize: '0.5625rem', fontWeight: isActive ? 700 : isDone ? 500 : 400,
                            color: isDone ? '#10b981' : isActive ? (isWaiting ? '#f59e0b' : '#6366f1') : isFailed ? '#ef4444' : '#475569',
                            display: isActive || isDone || isFailed ? 'inline' : 'none',
                          }}>
                            {step.label}
                          </span>
                        </div>
                      </React.Fragment>
                    );
                  })}

                  {/* Tool logs compact indicator */}
                  {toolLogs.length > 0 && (
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <Wrench size={10} style={{ color: 'hsl(var(--muted-foreground))' }} />
                      <span style={{
                        fontSize: '0.5625rem', fontFamily: 'monospace', color: '#6366f1',
                        maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {toolLogs[toolLogs.length - 1]?.data?.tool || ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ─── Full-width Conversation Panel ─── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>

            {/* Messages area */}
            <div 
              ref={chatContainerRef}
              onScroll={(e) => {
                const el = e.target;
                const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
                isNearBottomRef.current = nearBottom;
                setShowScrollToBottom(!nearBottom && chatMessages.length > 5);
              }}
              style={{
              flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '1.5rem 1rem',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: (pipelineRunning || project?.status === 'building' || project?.status === 'waiting_for_user' || project?.status === 'self_fixing' || project?.status === 'completed') ? '0' : '0.75rem 0.75rem 0 0',
              background: '#0f1219',
              position: 'relative',
              minHeight: 0,
            }} data-testid="chat-messages-container">
              {chatLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div className="spinner" style={{ margin: '0 auto 0.75rem', width: '24px', height: '24px' }} />
                    <p style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>Loading conversation...</p>
                  </div>
                </div>
              ) : chatMessages.length === 0 && !isStreaming ? (
                /* Empty state — welcoming */
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <div style={{ textAlign: 'center', maxWidth: '520px' }}>
                    <div style={{
                      width: '56px', height: '56px', borderRadius: '1rem', margin: '0 auto 1.25rem',
                      background: '#0f172a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 0 0 1px rgba(99,102,241,0.2), 0 8px 24px rgba(0,0,0,0.15)',
                    }}>
                      <Bot size={28} style={{ color: '#818cf8' }} />
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem', letterSpacing: '-0.01em', color: '#f8fafc' }}>
                      Hey{user?.email ? `, ${user.email.split('@')[0]}` : ''}! Few details needed:
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.75rem', lineHeight: 1.6 }}>
                      I'm your CodeFoundry copilot. Tell me what you'd like to build or improve, and I'll help you ship it.
                    </p>
                    {project?.status === 'completed' && (
                      <div style={{ fontSize: '0.8125rem', color: '#818cf8', marginBottom: '1rem', padding: '0.5rem 0.75rem', background: 'rgba(99,102,241,0.08)', borderRadius: '0.5rem', border: '1px solid rgba(99,102,241,0.15)' }}>
                        Your build is complete! Here are some features you could add next:
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                      {[
                        { text: 'Add a new feature', icon: Zap },
                        { text: 'Explain the architecture', icon: Code2 },
                        { text: 'Help me debug an issue', icon: AlertTriangle },
                        { text: 'Suggest improvements', icon: Sparkles },
                      ].map((item, pi) => (
                        <button
                          key={pi}
                          data-testid={`suggested-prompt-${pi}`}
                          onClick={() => { setChatInput(item.text); }}
                          style={{
                            padding: '0.625rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.75rem',
                            background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))',
                            color: 'hsl(var(--foreground))', cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            textAlign: 'left',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.background = 'rgba(99,102,241,0.05)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'hsl(var(--border))'; e.currentTarget.style.background = 'hsl(var(--card))'; }}
                        >
                          <item.icon size={14} style={{ color: '#818cf8', flexShrink: 0 }} />
                          <span>{item.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* Message list */
                <div style={{ maxWidth: '860px', margin: '0 auto' }}>
                  {chatMessages.map((msg, i) => {
                    // Context window separator — shows every 30 messages
                    const CONTEXT_WINDOW_SIZE = 30;
                    const windowNum = Math.floor(i / CONTEXT_WINDOW_SIZE);
                    const showWindowSeparator = i > 0 && i % CONTEXT_WINDOW_SIZE === 0;

                    const isUser = msg.role === 'user';
                    const isCheckpoint = msg.step === 'checkpoint' || msg.step === 'intake_questions';
                    const isIntakeQuestion = msg.step === 'intake_questions';
                    const isPreviewReady = msg.step === 'preview_ready';
                    const isError = msg.step === 'error';
                    const isSelfFix = msg.step?.startsWith('self_fix') || msg.agent === 'CodeFoundry Healer';
                    const isSelfFixSuccess = msg.step === 'self_fix_success';
                    const isSelfFixStart = msg.step === 'self_fix_start';
                    const isNewAgent = ['Planning Agent', 'Database Agent', 'Documentation Agent', 'API Design Agent', 'UI/UX Design Agent', 'Coding Agent', 'Testing Agent', 'Security Agent', 'DevOps Agent', 'Code Review Agent', 'Integration Agent'].includes(msg.agent);
                    const isStepDone = ['requirements', 'design', 'code_complete', 'scan_complete', 'deploy_complete', 'done', 'checkpoint_approved', 'self_fix_success', 'intake_response', 'plan_complete', 'database_complete', 'docs_complete', 'api_design_complete', 'uiux_design_complete', 'review_complete', 'integration_complete', 'detect_complete', 'batch_complete'].includes(msg.step);
                    const isFileMsg = msg.step === 'file_complete' || msg.step === 'code_progress';
                    const isBatchComplete = msg.step === 'batch_complete';
                    const isLastMsg = i === chatMessages.length - 1;
                    const isThinking = (msg.step === 'analyzing' || msg.step === 'self_fix_analyzing') && isLastMsg && pipelineRunning;
                    const isSystemEvent = msg.step === 'start' || ((isStepDone || isFileMsg) && !isBatchComplete);
                    const isWaiting = project?.status === 'waiting_for_user' && isLastMsg && isCheckpoint;
                    const narrativeType = msg.metadata?.narrative_type;
                    const stepNumber = msg.metadata?.step_number;
                    const isBlocked = msg.step === 'blocked' || narrativeType === 'proprietary_block';

                    // File generation progress counter for Coding Agent
                    let fileProgress = null;
                    if (isFileMsg && msg.agent === 'Coding Agent') {
                      const totalCodeMsgs = chatMessages.filter(m => (m.step === 'file_complete' || m.step === 'code_progress') && m.agent === 'Coding Agent');
                      const currentFileIdx = totalCodeMsgs.findIndex(m => m === msg);
                      const totalFilesInBuild = totalCodeMsgs.length;
                      if (currentFileIdx >= 0) {
                        fileProgress = { current: currentFileIdx + 1, total: totalFilesInBuild };
                      }
                    }

                    /* Proprietary content protection — blocked message */
                    if (isBlocked) {
                      return (
                        <div key={i} style={{
                          margin: '1rem 0', padding: '1.25rem',
                          background: 'rgba(239,68,68,0.04)',
                          border: '1px solid rgba(239,68,68,0.2)',
                          borderRadius: '0.75rem',
                          borderLeft: '3px solid #ef4444',
                        }} data-testid="proprietary-block-message">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            <div style={{
                              width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                              background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <ShieldAlert size={13} style={{ color: '#fff' }} />
                            </div>
                            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#ef4444' }}>Request Blocked</span>
                          </div>
                          <div style={{ fontSize: '14px', color: 'hsl(var(--foreground))', lineHeight: 1.8 }}>
                            {renderEnhancedMarkdown(msg.content)}
                          </div>
                        </div>
                      );
                    }

                    /* Compact system events — rendered as inline pills */
                    if (isSystemEvent && !isCheckpoint && !(msg.content?.length > 120)) {
                      return (
                        <React.Fragment key={i}>
                        {showWindowSeparator && (
                          <div data-testid={`context-window-${windowNum}`} style={{
                            display: 'flex', alignItems: 'center', gap: '0.75rem',
                            margin: '1.25rem 0', padding: '0.5rem 0',
                          }}>
                            <div style={{ height: '1px', flex: 1, background: 'linear-gradient(90deg, transparent, #6366f1, transparent)' }} />
                            <span style={{
                              fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.05em',
                              color: '#6366f1', padding: '0.25rem 0.75rem',
                              background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)',
                              borderRadius: '999px', whiteSpace: 'nowrap',
                            }}>
                              CONTEXT WINDOW {windowNum + 1}
                            </span>
                            <div style={{ height: '1px', flex: 1, background: 'linear-gradient(90deg, transparent, #6366f1, transparent)' }} />
                          </div>
                        )}
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          justifyContent: 'center', margin: '0.75rem 0',
                        }} data-testid={`chat-message-event-${i}`}>
                          <div style={{ height: '1px', flex: 1, background: 'rgba(255,255,255,0.06)' }} />
                          <span style={{
                            fontSize: '0.75rem', color: '#a1a9b8',
                            padding: '0.1875rem 0.6875rem', borderRadius: '999px',
                            background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)',
                            display: 'flex', alignItems: 'center', gap: '0.375rem', whiteSpace: 'nowrap',
                          }}>
                            {isStepDone && <CheckCircle size={11} style={{ color: '#10b981' }} />}
                            {isFileMsg && <FileCode size={11} style={{ color: '#60a5fa' }} />}
                            {msg.step === 'start' && <Play size={11} style={{ color: '#6366f1' }} />}
                            <span>{msg.content?.slice(0, 80) || msg.step}</span>
                            {fileProgress && (
                              <span style={{
                                fontSize: '0.6875rem', fontWeight: 700, color: '#818cf8',
                                background: 'rgba(99,102,241,0.15)', padding: '0.0625rem 0.375rem',
                                borderRadius: '0.25rem', fontFamily: '"JetBrains Mono", monospace',
                              }}>
                                {fileProgress.current}/{fileProgress.total}
                              </span>
                            )}
                          </span>
                          <div style={{ height: '1px', flex: 1, background: 'rgba(255,255,255,0.06)' }} />
                        </div>
                        </React.Fragment>
                      );
                    }

                    /* Narrative File Batch — compact card for batch_complete messages */
                    if (isBatchComplete && narrativeType === 'file_batch') {
                      const batchFiles = msg.metadata?.files || msg.files || [];
                      const batchNum = msg.metadata?.batch_num || '';
                      const totalBatches = msg.metadata?.total_batches || '';
                      return (
                        <React.Fragment key={i}>
                        <div style={{
                          margin: '0.5rem 0', padding: '0.625rem 0.875rem',
                          background: 'rgba(99,102,241,0.06)',
                          border: '1px solid rgba(99,102,241,0.15)',
                          borderRadius: '0.625rem',
                        }} data-testid={`batch-complete-${i}`}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                            <div style={{
                              width: '20px', height: '20px', borderRadius: '0.375rem', flexShrink: 0,
                              background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <CheckCircle size={11} style={{ color: '#fff' }} />
                            </div>
                            <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#ffffff' }}>
                              Batch {batchNum}/{totalBatches}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>
                              {batchFiles.length} file{batchFiles.length !== 1 ? 's' : ''} generated
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', paddingLeft: '1.75rem' }}>
                            {batchFiles.map((f, fi) => (
                              <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem' }}>
                                <FileCode size={12} style={{ color: '#818cf8', flexShrink: 0 }} />
                                <span style={{ fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontSize: '0.75rem', color: '#f8fafc' }}>
                                  {f.path}
                                </span>
                                <span style={{ fontSize: '0.6875rem', color: '#cbd5e1' }}>
                                  {f.lines} lines
                                </span>
                                <span style={{
                                  fontSize: '0.625rem', fontWeight: 600, padding: '0.0625rem 0.3125rem', borderRadius: '0.1875rem',
                                  background: f.confidence >= 85 ? 'rgba(16,185,129,0.12)' : f.confidence >= 70 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                                  color: f.confidence >= 85 ? '#10b981' : f.confidence >= 70 ? '#f59e0b' : '#ef4444',
                                }}>
                                  {f.confidence}%
                                </span>
                                {f.repaired && <span style={{ fontSize: '0.5625rem', color: '#f59e0b' }}>(repaired)</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                        </React.Fragment>
                      );
                    }

                    /* User messages */
                    if (isUser) {
                      return (
                        <React.Fragment key={i}>
                        {showWindowSeparator && (
                          <div data-testid={`context-window-${windowNum}`} style={{
                            display: 'flex', alignItems: 'center', gap: '0.75rem',
                            margin: '1.25rem 0', padding: '0.5rem 0',
                          }}>
                            <div style={{ height: '1px', flex: 1, background: 'linear-gradient(90deg, transparent, #6366f1, transparent)' }} />
                            <span style={{
                              fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.05em',
                              color: '#6366f1', padding: '0.25rem 0.75rem',
                              background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)',
                              borderRadius: '999px', whiteSpace: 'nowrap',
                            }}>
                              CONTEXT WINDOW {windowNum + 1}
                            </span>
                            <div style={{ height: '1px', flex: 1, background: 'linear-gradient(90deg, transparent, #6366f1, transparent)' }} />
                          </div>
                        )}
                        <div style={{
                          display: 'flex', justifyContent: 'flex-end', marginBottom: '1.25rem',
                        }} data-testid={`chat-message-user-${i}`}>
                          <div style={{ maxWidth: '85%' }}>
                            {msg.images && msg.images.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.375rem', justifyContent: 'flex-end' }}>
                                {msg.images.map((imgUrl, j) => (
                                  <img key={j} src={imgUrl.startsWith('/') ? `${API_URL}${imgUrl}` : imgUrl} alt="attachment"
                                    style={{ maxWidth: '200px', maxHeight: '140px', borderRadius: '0.75rem', cursor: 'pointer', objectFit: 'cover' }}
                                    onClick={() => window.open(imgUrl.startsWith('/') ? `${API_URL}${imgUrl}` : imgUrl, '_blank')}
                                    data-testid={`chat-image-${i}-${j}`}
                                  />
                                ))}
                              </div>
                            )}
                            {editingMsgIndex === i ? (
                              <InlineEditForm
                                originalContent={msg.content}
                                onSave={(newText) => {
                                  setEditingMsgIndex(null);
                                  if (newText.trim() && newText !== msg.content) {
                                    setChatInput(newText);
                                    clearDraft(id);
                                    setTimeout(() => { document.querySelector('[data-testid="chat-send-btn"]')?.click(); }, 100);
                                  }
                                }}
                                onCancel={() => setEditingMsgIndex(null)}
                              />
                            ) : (
                              <>
                                <div style={{
                                  padding: '0.75rem 1rem',
                                  borderRadius: '1.125rem 1.125rem 0.25rem 1.125rem',
                                  background: '#6366f1',
                                  color: 'white',
                                  fontSize: '14px', lineHeight: 1.8, wordSpacing: '0.06em', letterSpacing: '0.02em',
                                  wordBreak: 'break-word',
                                }}>
                                  {msg.content}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <MessageActions
                                    content={msg.content}
                                    isUser={true}
                                    isLast={false}
                                    onEdit={() => setEditingMsgIndex(i)}
                                  />
                                </div>
                              </>
                            )}
                            {msg.created_at && !editingMsgIndex && (
                              <div style={{ fontSize: '0.625rem', color: '#64748b', marginTop: '0.25rem', textAlign: 'right', opacity: 0.7 }}>
                                {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                        </div>
                        </React.Fragment>
                      );
                    }

                    /* AI / Agent messages */
                    return (
                      <React.Fragment key={i}>
                      {showWindowSeparator && (
                        <div data-testid={`context-window-${windowNum}`} style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          margin: '1.25rem 0', padding: '0.5rem 0',
                        }}>
                          <div style={{ height: '1px', flex: 1, background: 'linear-gradient(90deg, transparent, #6366f1, transparent)' }} />
                          <span style={{
                            fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.05em',
                            color: '#6366f1', padding: '0.25rem 0.75rem',
                            background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)',
                            borderRadius: '999px', whiteSpace: 'nowrap',
                          }}>
                            CONTEXT WINDOW {windowNum + 1}
                          </span>
                          <div style={{ height: '1px', flex: 1, background: 'linear-gradient(90deg, transparent, #6366f1, transparent)' }} />
                        </div>
                      )}

                      {/* Agent handoff divider */}
                      {isNewAgent && i > 0 && chatMessages[i - 1]?.agent !== msg.agent && (
                        <div data-testid={`agent-handoff-${i}`} style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          margin: '1.5rem 0 1rem',
                        }}>
                          <div style={{ height: '1px', flex: 1, background: `linear-gradient(90deg, transparent, ${(AGENT_COLORS[msg.agent] || AGENT_COLORS.default).accent}40, transparent)` }} />
                          <span style={{
                            fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.04em',
                            color: (AGENT_COLORS[msg.agent] || AGENT_COLORS.default).accent,
                            padding: '0.25rem 0.875rem',
                            background: `${(AGENT_COLORS[msg.agent] || AGENT_COLORS.default).accent}10`,
                            border: `1px solid ${(AGENT_COLORS[msg.agent] || AGENT_COLORS.default).accent}25`,
                            borderRadius: '999px', whiteSpace: 'nowrap',
                            display: 'flex', alignItems: 'center', gap: '0.375rem',
                          }}>
                            {(() => { const Icon = AGENT_ICON_MAP[msg.agent]; return Icon ? <Icon size={11} /> : null; })()}
                            {msg.agent}
                          </span>
                          <div style={{ height: '1px', flex: 1, background: `linear-gradient(90deg, transparent, ${(AGENT_COLORS[msg.agent] || AGENT_COLORS.default).accent}40, transparent)` }} />
                        </div>
                      )}

                      <div style={{
                        display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'flex-start',
                        ...(isSelfFix ? {
                          background: isSelfFixSuccess ? 'rgba(16,185,129,0.04)' : 'rgba(99,102,241,0.03)',
                          margin: '0.75rem -0.75rem 1.25rem',
                          padding: '0.75rem',
                          borderRadius: '0.75rem',
                          border: isSelfFixSuccess ? '1px solid rgba(16,185,129,0.15)' : '1px solid rgba(99,102,241,0.1)',
                        } : {}),
                      }} data-testid={`chat-message-ai-${i}`}>
                        {/* Agent Avatar with color theming */}
                        {(() => {
                          const agentTheme = AGENT_COLORS[msg.agent] || AGENT_COLORS.default;
                          const AgentIcon = isSelfFix ? Zap : (AGENT_ICON_MAP[msg.agent] || Bot);
                          const isActiveAgent = isLastMsg && pipelineRunning;
                          return (
                            <div style={{
                              width: '34px', height: '34px', borderRadius: '0.625rem', flexShrink: 0,
                              background: isSelfFix ? (isSelfFixSuccess ? '#064e3b' : '#1e1b4b') : agentTheme.bg,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: `1px solid ${isSelfFix ? 'rgba(168,85,247,0.3)' : agentTheme.ring}`,
                              marginTop: '0.125rem',
                              transition: 'box-shadow 0.3s',
                              ...(isActiveAgent ? { boxShadow: `0 0 0 3px ${agentTheme.ring}`, animation: 'agent-pulse 2s ease-in-out infinite' } : {}),
                            }}>
                              <AgentIcon size={16} style={{ color: isSelfFix ? (isSelfFixSuccess ? '#10b981' : '#a78bfa') : agentTheme.accent }} />
                            </div>
                          );
                        })()}

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
                            {stepNumber && (
                              <span style={{
                                fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.02em',
                                color: '#818cf8', background: 'rgba(99,102,241,0.12)',
                                padding: '0.125rem 0.4375rem', borderRadius: '0.25rem',
                                fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                              }}>
                                {stepNumber}/{msg.metadata?.total_steps || 16}
                              </span>
                            )}
                            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: isSelfFix ? '#c4b5fd' : '#ffffff' }}>
                              {isSelfFix ? 'CodeFoundry Healer' : msg.agent || 'CodeFoundry'}
                            </span>
                            {isSelfFix && !isSelfFixSuccess && (
                              <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#a78bfa', background: 'rgba(167,139,250,0.12)', padding: '0.0625rem 0.375rem', borderRadius: '0.25rem' }}>SELF-FIX</span>
                            )}
                            {isSelfFixSuccess && (
                              <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.12)', padding: '0.0625rem 0.375rem', borderRadius: '0.25rem' }}>FIXED</span>
                            )}
                            {isError && <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#ef4444', background: '#ef444415', padding: '0.0625rem 0.375rem', borderRadius: '0.25rem' }}>ERROR</span>}
                            {msg.created_at && (
                              <span style={{ fontSize: '0.6875rem', color: '#b0b8c8', opacity: 0.9 }}>
                                {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>

                          <div style={{
                            fontSize: '14px', lineHeight: 1.85, wordBreak: 'break-word',
                            color: '#f8fafc', wordSpacing: '0.06em', letterSpacing: '0.02em',
                            opacity: isThinking ? 0.6 : 1,
                          }}>
                            {isThinking ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Loader size={14} style={{ animation: 'spin 1s linear infinite', color: '#818cf8' }} />
                                <span style={{ fontStyle: 'italic', color: '#cbd5e1' }}>{msg.content}</span>
                              </div>
                            ) : (
                              <ExpandableMessage content={msg.content}>
                                <div className="chat-markdown">
                                  {renderEnhancedMarkdown(msg.content)}
                                </div>
                              </ExpandableMessage>
                            )}
                          </div>

                          {/* Message actions — copy, regenerate */}
                          {!isThinking && !isSystemEvent && msg.content && (
                            <MessageActions
                              content={msg.content}
                              isUser={false}
                              isLast={i === chatMessages.length - 1}
                              onRegenerate={() => {
                                setChatInput('Regenerate your last response');
                                setTimeout(() => { document.querySelector('[data-testid="chat-send-btn"]')?.click(); }, 100);
                              }}
                            />
                          )}

                          {/* Diff Viewer for self-fix patches */}
                          {msg.step === 'self_fix_applied' && msg.files && msg.files.length > 0 && (
                            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {msg.files.slice(0, 5).map((diff, di) => (
                                <DiffViewer key={di} oldCode={diff.old} newCode={diff.new} fileName={diff.path} />
                              ))}
                            </div>
                          )}

                          {/* Checkpoint — Glassmorphism Action Card */}
                          {isCheckpoint && isWaiting && (
                            <div style={{
                              marginTop: '0.75rem', padding: '1rem 1.25rem',
                              background: 'rgba(99,102,241,0.08)',
                              backdropFilter: 'blur(12px)',
                              WebkitBackdropFilter: 'blur(12px)',
                              border: '1px solid rgba(99,102,241,0.2)',
                              borderRadius: '0.75rem',
                              boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                            }} data-testid={`checkpoint-${i}`}>
                              {respondedCheckpoints.has(i) ? (
                                <div style={{ fontSize: '0.8125rem', color: '#10b981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                                  <CheckCircle size={16} /> Response submitted
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                  <button
                                    data-testid={`checkpoint-approve-${i}`}
                                    disabled={checkpointResponding}
                                    onClick={async () => {
                                      setCheckpointResponding(true);
                                      try {
                                        const res = await fetch(`${API_URL}/api/projects/${id}/checkpoint`, {
                                          method: 'POST', credentials: 'include',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ action: 'approved' }),
                                        });
                                        if (res.ok) { setRespondedCheckpoints(prev => new Set([...prev, i])); }
                                        else { const err = await res.json().catch(() => ({})); alert(err.detail || `Error: ${res.status}`); }
                                      } catch (e) { alert('Network error'); }
                                      finally { setCheckpointResponding(false); }
                                    }}
                                    style={{
                                      background: '#10b981', color: '#fff', border: 'none', padding: '0.5rem 1.25rem',
                                      borderRadius: '0.5rem', cursor: checkpointResponding ? 'wait' : 'pointer', fontWeight: 600, fontSize: '0.8125rem',
                                      display: 'flex', alignItems: 'center', gap: '0.375rem', opacity: checkpointResponding ? 0.6 : 1,
                                    }}
                                  >
                                    {checkpointResponding ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={14} />}
                                    Approve
                                  </button>
                                  <button
                                    data-testid={`checkpoint-modify-${i}`}
                                    disabled={checkpointResponding}
                                    onClick={async () => {
                                      const feedback = window.prompt('What would you like to change?');
                                      if (feedback) {
                                        setCheckpointResponding(true);
                                        try {
                                          const res = await fetch(`${API_URL}/api/projects/${id}/checkpoint`, {
                                            method: 'POST', credentials: 'include',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ action: 'modified', feedback }),
                                          });
                                          if (res.ok) { setRespondedCheckpoints(prev => new Set([...prev, i])); }
                                        } catch (e) { alert('Network error'); }
                                        finally { setCheckpointResponding(false); }
                                      }
                                    }}
                                    style={{
                                      background: 'transparent', color: '#f59e0b', border: '1px solid #f59e0b',
                                      padding: '0.5rem 1.25rem', borderRadius: '0.5rem', cursor: 'pointer',
                                      fontWeight: 600, fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.375rem',
                                    }}
                                  >
                                    <AlertTriangle size={14} /> Edit
                                  </button>
                                  <button
                                    data-testid={`checkpoint-regenerate-${i}`}
                                    disabled={checkpointResponding}
                                    onClick={async () => {
                                      setCheckpointResponding(true);
                                      try {
                                        const res = await fetch(`${API_URL}/api/projects/${id}/checkpoint`, {
                                          method: 'POST', credentials: 'include',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ action: 'modified', feedback: 'Please regenerate this from scratch with a different approach.' }),
                                        });
                                        if (res.ok) { setRespondedCheckpoints(prev => new Set([...prev, i])); }
                                      } catch (e) { alert('Network error'); }
                                      finally { setCheckpointResponding(false); }
                                    }}
                                    style={{
                                      background: 'transparent', color: '#6366f1', border: '1px solid #6366f1',
                                      padding: '0.5rem 1.25rem', borderRadius: '0.5rem', cursor: 'pointer',
                                      fontWeight: 600, fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.375rem',
                                    }}
                                  >
                                    <RefreshCw size={14} /> Regenerate
                                  </button>
                                  <button
                                    data-testid={`checkpoint-skip-${i}`}
                                    disabled={checkpointResponding}
                                    onClick={async () => {
                                      setCheckpointResponding(true);
                                      try {
                                        const res = await fetch(`${API_URL}/api/projects/${id}/checkpoint`, {
                                          method: 'POST', credentials: 'include',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ action: 'skipped' }),
                                        });
                                        if (res.ok) { setRespondedCheckpoints(prev => new Set([...prev, i])); }
                                      } catch (e) { console.error(e); }
                                      finally { setCheckpointResponding(false); }
                                    }}
                                    style={{
                                      background: 'transparent', color: '#64748b',
                                      border: '1px solid rgba(255,255,255,0.1)', padding: '0.5rem 1rem',
                                      borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.75rem',
                                    }}
                                  >
                                    Skip
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Preview Ready */}
                          {isPreviewReady && (
                            <div style={{ marginTop: '0.75rem' }} data-testid={`preview-card-${i}`}>
                              <button
                                onClick={() => setActiveTab('preview')}
                                style={{
                                  background: '#10b981', color: '#fff',
                                  border: 'none', padding: '0.5rem 1.25rem', borderRadius: '0.5rem',
                                  cursor: 'pointer', fontWeight: 600, fontSize: '0.8125rem',
                                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                                  transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#059669'}
                                onMouseLeave={e => e.currentTarget.style.background = '#10b981'}
                              >
                                <Eye size={16} /> Open Live Preview
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      </React.Fragment>
                    );
                  })}

                  {/* Streaming AI response */}
                  {isStreaming && streamingContent && (
                    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', alignItems: 'flex-start' }} data-testid="streaming-message">
                      {(() => {
                        const lastAgent = [...chatMessages].reverse().find(m => m.role === 'assistant' && m.agent)?.agent || '';
                        const theme = AGENT_COLORS[lastAgent] || AGENT_COLORS.default;
                        const Icon = AGENT_ICON_MAP[lastAgent] || Sparkles;
                        return (
                          <div style={{
                            width: '34px', height: '34px', borderRadius: '0.625rem', flexShrink: 0,
                            background: theme.bg,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: `1px solid ${theme.ring}`,
                            boxShadow: `0 0 0 3px ${theme.ring}`,
                            animation: 'pulse 2s ease-in-out infinite',
                          }}>
                            <Icon size={16} style={{ color: theme.accent }} />
                          </div>
                        );
                      })()}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
                          <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#ffffff' }}>CodeFoundry</span>
                          <span style={{ fontSize: '0.625rem', color: '#818cf8', background: 'rgba(99,102,241,0.12)', padding: '0.0625rem 0.375rem', borderRadius: '0.25rem', fontWeight: 600 }}>typing</span>
                        </div>
                        <div className="chat-markdown" style={{ fontSize: '14px', lineHeight: 1.85, wordBreak: 'break-word', color: '#f8fafc', wordSpacing: '0.06em', letterSpacing: '0.02em' }}>
                          {renderEnhancedMarkdown(streamingContent)}
                          <span style={{ display: 'inline-block', width: '2px', height: '1.1em', background: '#6366f1', animation: 'blink 1s step-end infinite', verticalAlign: 'text-bottom', marginLeft: '2px' }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Stop generation button — shows during streaming */}
                  {isStreaming && (
                    <StopButton onStop={() => {
                      setIsStreaming(false);
                      setChatSending(false);
                    }} />
                  )}

                  {/* Typing indicator — shows when waiting for response */}
                  {chatSending && !isStreaming && (
                    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', alignItems: 'flex-start' }} data-testid="typing-indicator">
                      <div style={{
                        width: '32px', height: '32px', borderRadius: '0.625rem', flexShrink: 0,
                        background: '#0f172a',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1px solid rgba(99,102,241,0.2)',
                      }}>
                        <Bot size={16} style={{ color: '#818cf8' }} />
                      </div>
                      <div style={{ padding: '0.75rem 0' }}>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.4s ease-in-out infinite' }} />
                          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#818cf8', animation: 'pulse 1.4s ease-in-out 0.2s infinite' }} />
                          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#a5b4fc', animation: 'pulse 1.4s ease-in-out 0.4s infinite' }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pipeline working indicator — agent-themed */}
                  {pipelineRunning && (
                    <div style={{
                      display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center',
                    }} data-testid="pipeline-thinking-indicator">
                      {(() => {
                        const lastAgent = [...chatMessages].reverse().find(m => m.role === 'assistant' && m.agent)?.agent || '';
                        const theme = AGENT_COLORS[lastAgent] || AGENT_COLORS.default;
                        const Icon = AGENT_ICON_MAP[lastAgent] || Loader;
                        
                        // Map current state to descriptive work message
                        const WORK_DESCRIPTIONS = {
                          'INTAKE': 'Analyzing your requirements...',
                          'PLANNING': 'Breaking down features and milestones...',
                          'PRD': 'Writing product requirements document...',
                          'ARCH': 'Designing system architecture...',
                          'DATABASE': 'Designing database schema...',
                          'API_DESIGN': 'Designing API endpoints...',
                          'UIUX_DESIGN': 'Creating UI/UX specifications...',
                          'CODE': 'Generating production code...',
                          'CODE_REVIEW': 'Reviewing code quality...',
                          'INTEGRATION': 'Setting up integrations...',
                          'TEST': 'Writing test suites...',
                          'SECURITY': 'Running security analysis...',
                          'DOCS': 'Generating documentation...',
                          'DEVOPS': 'Configuring deployment...',
                          'MONITOR': 'Setting up monitoring...',
                          'SELF_FIX': 'Auto-fixing detected issues...',
                        };
                        const workDesc = WORK_DESCRIPTIONS[project?.current_state] || 'Processing...';
                        
                        return (
                          <>
                            <div style={{
                              width: '34px', height: '34px', borderRadius: '0.625rem', flexShrink: 0,
                              background: theme.bg,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: `1px solid ${theme.ring}`,
                              animation: 'step-pulse 1.8s ease-in-out infinite',
                            }}>
                              <Icon size={16} style={{ color: theme.accent, animation: 'spin 2s linear infinite' }} />
                            </div>
                            <div style={{
                              padding: '0.5rem 1rem',
                              background: `${theme.accent}08`,
                              border: `1px solid ${theme.accent}15`,
                              borderRadius: '1rem',
                              display: 'flex', flexDirection: 'column', gap: '0.125rem',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.accent }}>
                                  {lastAgent || 'Agent'}
                                </span>
                                <span style={{ display: 'inline-flex', gap: '3px' }}>
                                  <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: theme.accent, animation: 'bounce 1.4s infinite', animationDelay: '0s' }} />
                                  <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: theme.accent, animation: 'bounce 1.4s infinite', animationDelay: '0.2s' }} />
                                  <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: theme.accent, animation: 'bounce 1.4s infinite', animationDelay: '0.4s' }} />
                                </span>
                              </div>
                              <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                                {workDesc}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  <div ref={chatEndRef} />
                  {showScrollToBottom && (
                    <button
                      data-testid="scroll-to-bottom-btn"
                      onClick={() => {
                        if (chatEndRef.current) {
                          chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
                          isNearBottomRef.current = true;
                          setShowScrollToBottom(false);
                        }
                      }}
                      style={{
                        position: 'sticky', bottom: '0.75rem', left: '50%', transform: 'translateX(-50%)',
                        display: 'flex', alignItems: 'center', gap: '0.375rem',
                        padding: '0.375rem 1rem', borderRadius: '9999px',
                        background: 'hsl(var(--accent))', color: 'white',
                        border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
                        zIndex: 10,
                      }}
                    >
                      <ArrowDown size={12} /> Jump to latest
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Image previews */}
            {chatImages.length > 0 && (
              <div style={{
                display: 'flex', gap: '0.5rem', padding: '0.5rem 1rem',
                background: 'hsl(var(--card))',
                borderLeft: '1px solid hsl(var(--border))', borderRight: '1px solid hsl(var(--border))',
              }} data-testid="chat-image-previews">
                {chatImages.map((img, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <img src={img.preview} alt="preview" style={{ width: '52px', height: '52px', borderRadius: '0.5rem', objectFit: 'cover', border: '1px solid hsl(var(--border))' }} />
                    <button
                      onClick={() => removeImage(idx)}
                      style={{
                        position: 'absolute', top: '-5px', right: '-5px',
                        width: '16px', height: '16px', borderRadius: '50%',
                        background: '#ef4444', color: 'white',
                        border: 'none', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', padding: 0,
                      }}
                      data-testid={`remove-image-${idx}`}
                    >
                      <X size={8} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Input area — copilot style */}
            <div style={{
              padding: '0.75rem 1rem',
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))', borderTop: chatImages.length > 0 ? 'none' : '1px solid hsl(var(--border))',
              borderRadius: '0 0 0.75rem 0.75rem',
            }}>
              {/* Mode pills + GitHub save */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.625rem' }}>
                <button
                  data-testid="chat-mode-toggle-chat"
                  onClick={() => setChatMode('chat')}
                  style={{
                    padding: '0.25rem 0.625rem', borderRadius: '0.375rem', fontSize: '0.6875rem', fontWeight: 600,
                    border: chatMode === 'chat' ? '1px solid #6366f1' : '1px solid hsl(var(--border))',
                    cursor: 'pointer', transition: 'all 0.15s',
                    background: chatMode === 'chat' ? 'rgba(99,102,241,0.1)' : 'transparent',
                    color: chatMode === 'chat' ? '#6366f1' : 'hsl(var(--muted-foreground))',
                  }}
                >
                  Chat
                </button>
                <button
                  data-testid="chat-mode-toggle-feature"
                  onClick={() => setChatMode('feature')}
                  style={{
                    padding: '0.25rem 0.625rem', borderRadius: '0.375rem', fontSize: '0.6875rem', fontWeight: 600,
                    border: chatMode === 'feature' ? '1px solid #f59e0b' : '1px solid hsl(var(--border))',
                    cursor: 'pointer', transition: 'all 0.15s',
                    background: chatMode === 'feature' ? 'rgba(245,158,11,0.1)' : 'transparent',
                    color: chatMode === 'feature' ? '#f59e0b' : 'hsl(var(--muted-foreground))',
                  }}
                >
                  Build Feature
                </button>

                {/* GitHub save button — right side */}
                <div style={{ marginLeft: 'auto' }}>
                  <button
                    data-testid="chat-github-save-toggle"
                    onClick={() => { setShowGitPanel(!showGitPanel); if (!showGitPanel) { if (!githubStatus) fetchGitHubStatus(); fetchVerificationStatus(); if (selectedOrg) fetchRepos(selectedOrg); } }}
                    style={{
                      padding: '0.25rem 0.625rem', borderRadius: '0.375rem', fontSize: '0.6875rem', fontWeight: 600,
                      border: showGitPanel ? '1px solid #24292f' : '1px solid hsl(var(--border))',
                      cursor: 'pointer', transition: 'all 0.15s',
                      background: showGitPanel ? 'rgba(36,41,47,0.1)' : 'transparent',
                      color: showGitPanel ? '#24292f' : 'hsl(var(--muted-foreground))',
                      display: 'flex', alignItems: 'center', gap: '0.375rem',
                    }}
                  >
                    <Github size={12} /> Save to GitHub
                  </button>
                </div>
              </div>

              {/* GitHub save panel — inline dropdown */}
              {showGitPanel && (
                <div style={{
                  marginBottom: '0.625rem', padding: '0.75rem',
                  background: 'hsl(var(--secondary))', borderRadius: '0.5rem',
                  border: '1px solid hsl(var(--border))',
                }} data-testid="chat-github-panel">
                  {!githubStatus?.connected ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Github size={16} style={{ color: 'hsl(var(--muted-foreground))', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', flex: 1 }}>
                        Connect GitHub in Settings to push code
                      </span>
                      <a href="/settings" style={{
                        fontSize: '0.6875rem', fontWeight: 600, color: '#6366f1', textDecoration: 'none',
                        padding: '0.25rem 0.625rem', borderRadius: '0.375rem', border: '1px solid #6366f1',
                      }} data-testid="chat-github-connect">Connect</a>
                    </div>
                  ) : (
                    <div>
                      {/* Compact repo selector */}
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: pushResult ? '0.5rem' : 0 }}>
                        {/* Org */}
                        <div style={{ flex: '0 0 auto', minWidth: '120px' }}>
                          <label style={{ fontSize: '0.625rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', display: 'block', marginBottom: '0.125rem' }}>Account</label>
                          <select
                            value={selectedOrg}
                            onChange={(e) => { setSelectedOrg(e.target.value); setSelectedRepo(null); fetchRepos(e.target.value); }}
                            style={{
                              width: '100%', padding: '0.3rem 0.5rem', fontSize: '0.75rem',
                              border: '1px solid hsl(var(--border))', borderRadius: '0.375rem',
                              background: 'hsl(var(--background))', color: 'hsl(var(--foreground))',
                            }}
                            data-testid="chat-org-selector"
                          >
                            {orgs.map(org => (
                              <option key={org.login} value={org.login}>{org.login}</option>
                            ))}
                          </select>
                        </div>

                        {/* Repo */}
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.125rem' }}>
                            <label style={{ fontSize: '0.625rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>Repository</label>
                            {!showNewRepo && (
                              <button onClick={() => setShowNewRepo(true)} style={{
                                fontSize: '0.5625rem', fontWeight: 600, color: '#6366f1',
                                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                              }} data-testid="chat-new-repo-toggle">+ New</button>
                            )}
                          </div>
                          {showNewRepo ? (
                            <div style={{ display: 'flex', gap: '0.375rem' }}>
                              <input
                                placeholder="repo-name"
                                value={newRepoName}
                                onChange={(e) => setNewRepoName(e.target.value)}
                                style={{
                                  flex: 1, padding: '0.3rem 0.5rem', fontSize: '0.75rem',
                                  border: '1px solid hsl(var(--border))', borderRadius: '0.375rem',
                                  background: 'hsl(var(--background))', fontFamily: 'monospace',
                                }}
                                data-testid="chat-new-repo-input"
                              />
                              <button
                                onClick={() => { createNewRepo(); setShowNewRepo(false); }}
                                disabled={creatingRepo || !newRepoName.trim()}
                                style={{
                                  padding: '0.3rem 0.5rem', fontSize: '0.6875rem', fontWeight: 600,
                                  background: '#6366f1', color: 'white', border: 'none',
                                  borderRadius: '0.375rem', cursor: 'pointer', whiteSpace: 'nowrap',
                                }}
                                data-testid="chat-create-repo-btn"
                              >{creatingRepo ? '...' : 'Create'}</button>
                              <button onClick={() => setShowNewRepo(false)} style={{
                                padding: '0.3rem 0.5rem', fontSize: '0.6875rem',
                                background: 'none', border: '1px solid hsl(var(--border))',
                                borderRadius: '0.375rem', cursor: 'pointer', color: 'hsl(var(--muted-foreground))',
                              }}>Cancel</button>
                            </div>
                          ) : (
                            <select
                              value={selectedRepo?.full_name || ''}
                              onChange={(e) => {
                                const repo = repos.find(r => r.full_name === e.target.value);
                                if (repo) { setSelectedRepo(repo); fetchBranches(repo.full_name); }
                              }}
                              style={{
                                width: '100%', padding: '0.3rem 0.5rem', fontSize: '0.75rem',
                                border: '1px solid hsl(var(--border))', borderRadius: '0.375rem',
                                background: 'hsl(var(--background))', color: 'hsl(var(--foreground))',
                              }}
                              data-testid="chat-repo-selector"
                            >
                              <option value="">Select repo...</option>
                              {repos.map(repo => (
                                <option key={repo.full_name} value={repo.full_name}>{repo.name}</option>
                              ))}
                            </select>
                          )}
                        </div>

                        {/* Branch */}
                        {selectedRepo && (
                          <div style={{ flex: '0 0 auto', minWidth: '100px' }}>
                            <label style={{ fontSize: '0.625rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', display: 'block', marginBottom: '0.125rem' }}>Branch</label>
                            <select
                              value={selectedBranch}
                              onChange={(e) => setSelectedBranch(e.target.value)}
                              style={{
                                width: '100%', padding: '0.3rem 0.5rem', fontSize: '0.75rem',
                                border: '1px solid hsl(var(--border))', borderRadius: '0.375rem',
                                background: 'hsl(var(--background))', color: 'hsl(var(--foreground))',
                              }}
                              data-testid="chat-branch-selector"
                            >
                              {branches.map(b => (
                                <option key={b.name} value={b.name}>{b.name}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Verify + Push buttons */}
                        <div style={{ display: 'flex', gap: '0.375rem', alignSelf: 'flex-end' }}>
                          <button
                            onClick={() => { triggerVerification(); fetchVerificationStatus(); }}
                            disabled={verifyLoading}
                            style={{
                              padding: '0.3rem 0.625rem', fontSize: '0.6875rem', fontWeight: 600,
                              background: verifyLoading ? 'hsl(var(--border))' : 'rgba(99,102,241,0.1)',
                              color: verifyLoading ? 'hsl(var(--muted-foreground))' : '#6366f1',
                              border: '1px solid rgba(99,102,241,0.2)', borderRadius: '0.375rem',
                              cursor: verifyLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap',
                              display: 'flex', alignItems: 'center', gap: '0.25rem',
                            }}
                            data-testid="chat-verify-button"
                          >
                            {verifyLoading ? <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Shield size={11} />}
                            {verifyLoading ? 'Verifying...' : 'Verify'}
                          </button>
                          <button
                            onClick={pushToGitHub}
                            disabled={pushLoading || !selectedRepo}
                            title={!selectedRepo ? 'Select a repository first' : 'Push to GitHub'}
                            style={{
                              padding: '0.3rem 0.875rem', fontSize: '0.75rem', fontWeight: 600,
                              background: (!selectedRepo || pushLoading) ? 'hsl(var(--border))' : '#24292f',
                              color: (!selectedRepo || pushLoading) ? 'hsl(var(--muted-foreground))' : 'white',
                              border: 'none', borderRadius: '0.375rem',
                              cursor: (!selectedRepo || pushLoading) ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.375rem',
                              transition: 'all 0.15s',
                            }}
                            data-testid="chat-push-button"
                          >
                            {pushLoading ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={12} />}
                            {pushLoading ? 'Pushing...' : 'Push'}
                          </button>
                        </div>
                      </div>

                      {/* Verification checklist */}
                      {verification && verification.status !== 'not_started' && (
                        <div style={{
                          marginTop: '0.5rem', padding: '0.5rem 0.625rem',
                          background: 'hsl(var(--background))', borderRadius: '0.375rem',
                          border: '1px solid hsl(var(--border))',
                        }} data-testid="verification-checklist">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                            <span style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'hsl(var(--muted-foreground))' }}>
                              Compile Gate {verification.score && `(${verification.score})`}
                            </span>
                            {verification.push_ready && (
                              <span style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '0.0625rem 0.375rem', borderRadius: '0.25rem' }}>
                                PUSH READY
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.25rem' }}>
                            {Object.entries(verification.gates || {}).map(([key, gate]) => (
                              <div key={key} style={{
                                display: 'flex', alignItems: 'center', gap: '0.25rem',
                                fontSize: '0.625rem', padding: '0.1875rem 0.375rem',
                                borderRadius: '0.25rem',
                                background: gate.status === 'passed' ? 'rgba(16,185,129,0.06)' :
                                  gate.status === 'failed' ? 'rgba(239,68,68,0.06)' :
                                  gate.status === 'running' ? 'rgba(99,102,241,0.06)' :
                                  gate.status === 'skipped' ? 'rgba(156,163,175,0.06)' : 'transparent',
                              }} data-testid={`gate-${key}`} title={gate.message || ''}>
                                {gate.status === 'passed' && <CheckCircle size={10} style={{ color: '#10b981', flexShrink: 0 }} />}
                                {gate.status === 'failed' && <XCircle size={10} style={{ color: '#ef4444', flexShrink: 0 }} />}
                                {gate.status === 'running' && <Loader size={10} style={{ color: '#6366f1', flexShrink: 0, animation: 'spin 1s linear infinite' }} />}
                                {gate.status === 'skipped' && <MinusCircle size={10} style={{ color: '#9ca3af', flexShrink: 0 }} />}
                                {gate.status === 'pending' && <div style={{ width: '10px', height: '10px', borderRadius: '50%', border: '1.5px solid hsl(var(--border))', flexShrink: 0 }} />}
                                <span style={{
                                  color: gate.status === 'failed' ? '#ef4444' : 'hsl(var(--foreground))',
                                  fontWeight: gate.status === 'failed' ? 600 : 400,
                                }}>{gate.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Push result */}
                      {pushResult && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.375rem',
                          padding: '0.375rem 0.625rem', borderRadius: '0.375rem',
                          background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)',
                        }} data-testid="chat-push-success">
                          <CheckCircle size={13} style={{ color: '#10b981', flexShrink: 0 }} />
                          <span style={{ fontSize: '0.6875rem', color: '#10b981', fontWeight: 600 }}>Pushed successfully</span>
                          {pushResult.commit_url && (
                            <a href={pushResult.commit_url} target="_blank" rel="noreferrer"
                              style={{ fontSize: '0.6875rem', color: '#6366f1', marginLeft: 'auto' }}
                            >View commit</a>
                          )}
                        </div>
                      )}
                      {pushError && (
                        <div style={{
                          padding: '0.375rem 0.625rem', borderRadius: '0.375rem', marginTop: '0.375rem',
                          background: 'rgba(239,68,68,0.08)', fontSize: '0.6875rem', color: '#ef4444',
                        }} data-testid="chat-push-error">{pushError}</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Input row */}
              {/* Sticky "Action Required" bar when waiting for user */}
              {project?.status === 'waiting_for_user' && (() => {
                const lastCheckpointIdx = [...chatMessages].map((m, i) => ({ ...m, i })).reverse().find(m => m.step?.startsWith('checkpoint'))?.i;
                const hasResponded = lastCheckpointIdx !== undefined && respondedCheckpoints.has(lastCheckpointIdx);
                if (hasResponded) return null;
                return (
                  <div data-testid="action-required-bar" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
                    padding: '0.625rem 1rem', marginBottom: '0.5rem',
                    background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(251,191,36,0.06))',
                    border: '1px solid rgba(245,158,11,0.35)',
                    borderRadius: '0.75rem',
                    animation: 'pulse 2s ease-in-out infinite',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
                      <div style={{
                        width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                        background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <MessageCircle size={14} style={{ color: '#f59e0b' }} />
                      </div>
                      <div>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#f59e0b' }}>Action Required</span>
                        <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginLeft: '0.5rem' }}>
                          The agent is waiting for your response to continue building
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.375rem', flexShrink: 0 }}>
                      <button
                        data-testid="action-scroll-to-checkpoint"
                        onClick={() => {
                          if (lastCheckpointIdx !== undefined) {
                            const el = document.querySelector(`[data-testid="checkpoint-${lastCheckpointIdx}"]`);
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }}
                        style={{
                          padding: '0.375rem 0.75rem', background: 'transparent', border: '1px solid rgba(245,158,11,0.4)',
                          borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
                          color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.25rem',
                        }}
                      >
                        <ArrowUp size={12} /> View
                      </button>
                      <button
                        data-testid="action-quick-approve"
                        disabled={checkpointResponding}
                        onClick={async () => {
                          setCheckpointResponding(true);
                          try {
                            const res = await fetch(`${API_URL}/api/projects/${id}/checkpoint`, {
                              method: 'POST', credentials: 'include',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ action: 'approved' }),
                            });
                            if (res.ok && lastCheckpointIdx !== undefined) {
                              setRespondedCheckpoints(prev => new Set([...prev, lastCheckpointIdx]));
                            } else {
                              const err = await res.json().catch(() => ({}));
                              alert(err.detail || `Error: ${res.status}`);
                            }
                          } catch (e) { alert('Network error'); }
                          finally { setCheckpointResponding(false); }
                        }}
                        style={{
                          padding: '0.375rem 0.75rem', background: '#10b981', border: 'none',
                          borderRadius: '0.375rem', cursor: checkpointResponding ? 'wait' : 'pointer',
                          fontSize: '0.75rem', fontWeight: 600, color: '#fff',
                          display: 'flex', alignItems: 'center', gap: '0.25rem',
                          opacity: checkpointResponding ? 0.6 : 1,
                        }}
                      >
                        <CheckCircle size={12} /> Approve & Continue
                      </button>
                    </div>
                  </div>
                );
              })()}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', position: 'relative' }}>
                {/* Prompt Templates Panel */}
                {showPromptTemplates && (
                  <PromptTemplatesPanel
                    onSelect={(prompt) => {
                      setChatInput(prompt);
                      saveDraft(id, prompt);
                    }}
                    onClose={() => setShowPromptTemplates(false)}
                  />
                )}
                <input
                  type="file" ref={fileInputRef} onChange={handleImageUpload}
                  accept="image/*" multiple style={{ display: 'none' }}
                  data-testid="chat-file-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: '0.5rem', flexShrink: 0, background: 'none', border: 'none',
                    cursor: 'pointer', color: '#64748b',
                    borderRadius: '0.375rem', transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#e2e8f0'}
                  onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
                  title="Attach image"
                  data-testid="chat-attach-button"
                >
                  <Paperclip size={18} />
                </button>
                <button
                  onClick={() => setShowPromptTemplates(!showPromptTemplates)}
                  style={{
                    padding: '0.5rem', flexShrink: 0, background: showPromptTemplates ? 'rgba(99,102,241,0.1)' : 'none',
                    border: showPromptTemplates ? '1px solid rgba(99,102,241,0.3)' : 'none',
                    cursor: 'pointer', color: showPromptTemplates ? '#818cf8' : '#64748b',
                    borderRadius: '0.375rem', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!showPromptTemplates) e.currentTarget.style.color = '#e2e8f0'; }}
                  onMouseLeave={e => { if (!showPromptTemplates) e.currentTarget.style.color = '#64748b'; }}
                  title="Prompt templates"
                  data-testid="prompt-templates-btn"
                >
                  <Sparkles size={18} />
                </button>
                <textarea
                  placeholder={
                    isBuildDisabled ? "Build in progress... please wait" :
                    project?.status === 'waiting_for_user' ? "Reply to the agent... (or type 'go' to accept defaults)" :
                    chatMode === 'feature' ? "Describe the feature you want to build..." :
                    "Ask anything about your project... (Ctrl+V to paste screenshots)"
                  }
                  value={chatInput}
                  disabled={isBuildDisabled}
                  onChange={(e) => { setChatInput(e.target.value); saveDraft(id, e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px'; }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                  onPaste={handlePasteImage}
                  onDrop={handleDropImage}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  rows={1}
                  style={{
                    flex: 1, resize: 'none', minHeight: '40px', maxHeight: '150px',
                    fontSize: '0.875rem', margin: 0, padding: '0.5rem 0.75rem',
                    border: `1px solid ${dragOver ? '#6366f1' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '0.5rem',
                    background: dragOver ? 'rgba(99,102,241,0.1)' : '#161b26',
                    color: '#e2e8f0',
                    lineHeight: 1.5, outline: 'none', fontFamily: 'inherit',
                    transition: 'border-color 0.15s, background 0.15s',
                    opacity: isBuildDisabled ? 0.5 : 1,
                    cursor: isBuildDisabled ? 'not-allowed' : 'text',
                  }}
                  onFocus={e => e.target.style.borderColor = '#6366f1'}
                  onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; setDragOver(false); }}
                  data-testid="chat-input"
                />
                <button
                  onClick={sendChatMessage}
                  disabled={chatSending || (!chatInput.trim() && chatImages.length === 0)}
                  style={{
                    padding: '0.5rem 0.625rem', flexShrink: 0,
                    background: (chatSending || (!chatInput.trim() && chatImages.length === 0)) ? '#1e293b' : '#6366f1',
                    color: (chatSending || (!chatInput.trim() && chatImages.length === 0)) ? '#475569' : 'white',
                    border: 'none', borderRadius: '0.5rem', cursor: chatSending ? 'wait' : 'pointer',
                    transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  data-testid="chat-send-btn"
                >
                  {chatSending ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                </button>
              </div>
            </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
